import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Bot,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock3,
  Database,
  Edit3,
  Eye,
  EyeOff,
  Film,
  Folder,
  FolderOpen,
  Globe,
  HardDrive,
  HelpCircle,
  Key,
  Loader,
  Mail,
  MapPin,
  PackageOpen,
  Plus,
  QrCode,
  RefreshCw,
  Settings,
  Settings2,
  Save,
  Shield,
  DollarSign,
  Gauge,
  TrendingUp,
  Download,
  AlertTriangle,
  Star,
  Clock,
  Hammer,
  History,
  RotateCcw,
  Trash2,
  Upload,
  UserCog,
  Users,
  X,
  Zap,
  icons as allLucideIcons,
  UserPlus,
} from 'lucide-react';
import { Icon } from './Icon';
import { useToast } from '../toastContext';
import { useConfirm } from '../confirmContext';
import UsersPanel from './UsersPanel';
import AccessControlBoard from './AccessControlBoard';
import ProjectIntakePanel from './ProjectIntakePanel';
import LoadFailure from './LoadFailure';
import Button from './ui/Button';
import Input from './ui/Input';
import Badge from './ui/Badge';
import { Z } from '../constants/tokens';

const ADMIN_HELP_ICONS = { Settings, Settings2, Users, Key, KeyRound: Key, Globe, Shield, Mail, Clock, Clock3, Activity, Bot, MapPin, RefreshCw, HelpCircle, Loader, Database, Star, Hammer };

// All Lucide icon names sorted alphabetically for the icon reference & picker
const ALL_ICON_NAMES = Object.keys(allLucideIcons).sort();

// Icon picker toolbar component — shows above textarea, lets user search & insert icons
function IconPickerToolbar({ theme, onInsert }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return ALL_ICON_NAMES.slice(0, 80);
    return ALL_ICON_NAMES.filter(n => n.toLowerCase().includes(q)).slice(0, 80);
  }, [search]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        title="Insert icon shorthand"
        style={{
          padding: '3px 7px', fontSize: '0.58rem', fontWeight: 700, borderRadius: '5px',
          cursor: 'pointer', color: '#2563eb', background: '#2563eb14',
          border: '1px solid #2563eb33', display: 'flex', alignItems: 'center', gap: '3px',
        }}
      >
        <Plus size={9} /> Icon
      </button>
      {open && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: '4px', zIndex: Z.dropdown,
          width: '340px', maxHeight: '320px', background: theme.sidebar || '#fff',
          border: `1px solid ${theme.border}`, borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <Input
              theme={theme}
              size="sm"
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search icons..."
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 8px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '3px' }}>
            {filtered.map(name => {
              const LucideIcon = allLucideIcons[name];
              if (!LucideIcon) return null;
              return (
                <button
                  key={name}
                  onClick={() => { onInsert(`<${name}>`); setOpen(false); }}
                  title={name}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                    padding: '5px 2px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: theme.text,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.card}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <LucideIcon size={16} color="#2563eb" />
                  <span style={{ fontSize: '0.44rem', color: theme.textSub, lineHeight: 1.1, textAlign: 'center', wordBreak: 'break-all', maxWidth: '54px' }}>
                    {name}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', fontSize: '0.66rem', color: theme.textSub, padding: '12px' }}>
                No icons match "{search}"
              </div>
            )}
          </div>
          <div style={{ padding: '4px 8px', borderTop: `1px solid ${theme.border}`, fontSize: '0.54rem', color: theme.textSub, textAlign: 'center' }}>
            {ALL_ICON_NAMES.length} icons available · Showing {filtered.length}
          </div>
        </div>
      )}
    </div>
  );
}
function renderAdminHelpText(text, theme) {
  if (!text) return null;
  const parts = text.split(/(<[A-Z][A-Za-z0-9]*>)/g);
  return parts.map((part, i) => {
    const m = part.match(/^<([A-Z][A-Za-z0-9]*)>$/);
    if (m) {
      const LucideIcon = ADMIN_HELP_ICONS[m[1]] || allLucideIcons[m[1]];
      if (LucideIcon) return <span key={i} style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', margin: '0 2px' }}><LucideIcon size={13} color="#f59e0b" /></span>;
    }
    return <span key={i}>{part}</span>;
  });
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  if (total === 0) return '0s';
  const years   = Math.floor(total / 31536000);
  const days    = Math.floor((total % 31536000) / 86400);
  const hours   = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs    = total % 60;
  const parts = [];
  if (years > 0)   parts.push(`${years}y`);
  if (days > 0)    parts.push(`${days}d`);
  if (hours > 0)   parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && !years && !days) parts.push(`${secs}s`);  // skip seconds for large durations
  return parts.join(' ') || '0s';
}

function formatDurationMs(ms) {
  return formatDuration(Math.round((ms || 0) / 1000));
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatNumber(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString();
}

function formatUsd(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '$0.00';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same currency convention as formatUsd (2 decimals), but keeps sub-cent AI costs
// legible instead of collapsing them to $0.00. Used for granular AI spend figures.
function formatUsdFine(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '$0.00';
  const digits = (num !== 0 && Math.abs(num) < 0.01) ? 4 : 2;
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatActionLabel(area, action) {
  return [area, action].filter(Boolean).join(' / ');
}

function SummaryTile({ theme, icon, label, value, tint, onAction, actionIcon, actionTitle, onClick }) {
  return (
    <div onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(e); } } : undefined}
      style={{
      minWidth: '130px',
      flex: '1 1 130px',
      padding: '10px 12px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      background: theme.sidebar,
      textAlign: 'center',
      position: 'relative',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = tint || theme.glossaryAccent; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}
    >
      {onAction && (
        <button onClick={(e) => { e.stopPropagation(); onAction(e); }} title={actionTitle || 'Fix'}
          style={{ position: 'absolute', top: '6px', right: '6px', background: 'none', border: 'none', cursor: 'pointer', color: tint || theme.textSub, opacity: 0.6, padding: 0, display: 'flex' }}
        ><Icon icon={actionIcon || RefreshCw} size={10} /></button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '6px', color: tint || theme.textSub }}>
        <Icon icon={icon} size={12} color={tint || theme.textSub} />
        <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: theme.text }}>{value}</div>
    </div>
  );
}

function AdminCard({ theme, title, icon, accent, isOpen, onToggle, children, extra }) {
  return (
    <section style={{
      background: theme.sidebar,
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: theme.text,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Icon icon={icon} size={14} color={accent} />
          <span style={{ fontSize: '0.88rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {extra}
          <Icon icon={isOpen ? ChevronDown : ChevronRight} size={15} color={theme.textSub} />
        </span>
      </button>
      {isOpen && (
        <div style={{ padding: '0 16px 14px' }}>
          {children}
        </div>
      )}
    </section>
  );
}

export default function AdminDashboardPage({
  axiosAuth,
  currentUser,
  theme,
  isDarkMode,
  BACKEND_URL,
  videoList = [],
  accessRequests = [],
  onGoToAccessRequests,
  onOpenGlossary,
  onOpenQR,
  onOpenConverter,
  onWorkingDirectoryChanged,
}) {
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [intakeCount, setIntakeCount] = useState(0);
  const [activeMenu, setActiveMenu] = useState('statistics');
  const [cardOpen, setCardOpen] = useState({
    overview: false,
    tracking: false,
    activity: false,
    locations: false,
    logs: false,
    ai: false,
    aiCostControls: false,
    aiCostBreakdown: false,
    aiRuns: false,
    workspace: false,
    tools: false,
    users: false,
    access: false,
    acl: false,
    apiKeys: false,
    dbConfig: false,
    helpMenus: false,
  });

  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showAdminHelp, setShowAdminHelp] = useState(false);
  const [adminHelpItems, setAdminHelpItems] = useState(null);

  // ── System status (live) ──
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const fetchSystemStatus = useCallback(() => {
    setStatusLoading(true);
    axiosAuth.get('/system/status')
      .then(r => setSystemStatus(r.data))
      .catch(() => setSystemStatus(null))
      .finally(() => setStatusLoading(false));
  }, [axiosAuth]);
  useEffect(() => {
    fetchSystemStatus();
    const iv = setInterval(fetchSystemStatus, 30000); // refresh every 30s
    return () => clearInterval(iv);
  }, [fetchSystemStatus]);

  // Shared cleanup handler for session/tracking tiles
  const runSessionCleanup = () => {
    const tok = sessionStorage.getItem('gwv_token');
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
    fetch(`${BACKEND_URL}/admin/cleanup?dry_run=true`, { method: 'POST', headers })
      .then(r => r.json())
      .then(async data => {
        const parts = [];
        if (data.stale_sessions > 0) parts.push(`${data.stale_sessions} stale sessions (open >4h)`);
        if (data.inflated_sessions_over_4h > 0) parts.push(`${data.inflated_sessions_over_4h} inflated sessions (duration >4h — will be capped)`);
        if (data.stale_activity_segments > 0) parts.push(`${data.stale_activity_segments} orphaned activity segments`);
        if (data.stuck_ai_traces > 0) parts.push(`${data.stuck_ai_traces} stuck AI traces`);
        if (parts.length === 0) { pushToast('admin-session-cleanup', 'info', 'Nothing to clean up — all session data looks healthy.'); return; }
        if (!await confirm({
          message: `Found:\n\n• ${parts.join('\n• ')}\n\nClean up now? Inflated sessions will be capped at 4 hours.`,
          title: 'Clean up stale data?',
          confirmLabel: 'Clean Up',
          destructive: true,
        })) return;
        fetch(`${BACKEND_URL}/admin/cleanup?dry_run=false`, { method: 'POST', headers })
          .then(r => r.json())
          // Kept as alert() on purpose: it must block until dismissed, because the
          // reload on the next statement would otherwise tear down a toast instantly.
          .then(() => { alert('Cleanup complete. Reload the page to see updated totals.'); window.location.reload(); })
          .catch(err => pushToast('admin-session-cleanup', 'error', `Cleanup failed: ${err.message}`));
      })
      .catch(err => pushToast('admin-session-cleanup', 'error', `Dry run failed: ${err.message}`));
  };

  // Help Menus state
  const [helpMenus, setHelpMenus] = useState([]);
  const [helpMenusLoading, setHelpMenusLoading] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null); // menu object being edited
  const [menuDraft, setMenuDraft] = useState({ id: '', title: '', items: [] });
  const [helpTab, setHelpTab] = useState('menus'); // 'menus' | 'icons' | 'shorthand'
  const [iconSearch, setIconSearch] = useState('');
  const [copiedIcon, setCopiedIcon] = useState(null);
  const [menuHistory, setMenuHistory] = useState([]); // history entries for a menu
  const [menuHistoryError, setMenuHistoryError] = useState(false);
  const [historyMenuId, setHistoryMenuId] = useState(null); // which menu's history is shown
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHelpMenus = useCallback(() => {
    setHelpMenusLoading(true);
    axiosAuth.get('/admin/help-menus')
      .then(r => setHelpMenus(r.data || []))
      .catch(() => {})
      .finally(() => setHelpMenusLoading(false));
  }, [axiosAuth]);

  const loadMenuHistory = useCallback((menuId) => {
    setHistoryLoading(true);
    setHistoryMenuId(menuId);
    axiosAuth.get(`/admin/help-menus/${menuId}/history`)
      .then(r => { setMenuHistory(r.data || []); setMenuHistoryError(false); })
      .catch(() => setMenuHistoryError(true))
      .finally(() => setHistoryLoading(false));
  }, [axiosAuth]);

  const revertMenu = useCallback(async (menuId, historyId) => {
    if (!await confirm({
      message: 'Revert to this version? The current version will be saved to history.',
      title: 'Revert help menu?',
      confirmLabel: 'Revert',
      destructive: false,
    })) return;
    axiosAuth.post(`/admin/help-menus/${menuId}/revert/${historyId}`)
      .then(() => { loadHelpMenus(); loadMenuHistory(menuId); })
      .catch(err => pushToast('admin-menu-revert-error', 'error', 'Revert failed: ' + (err.response?.data?.detail || err.message)));
  }, [axiosAuth, loadHelpMenus, loadMenuHistory, pushToast, confirm]);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeyEditing, setApiKeyEditing] = useState(null);   // key_name being edited
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyMsg, setApiKeyMsg] = useState({});              // { key_name: { type, text } }
  const [apiKeyVisible, setApiKeyVisible] = useState({});      // { key_name: bool }

  const loadApiKeys = useCallback(() => {
    setApiKeysLoading(true);
    axiosAuth.get('/admin/api-keys')
      .then(r => setApiKeys(r.data || []))
      .catch(() => {})
      .finally(() => setApiKeysLoading(false));
  }, [axiosAuth]);

  const saveApiKey = async (keyName) => {
    setApiKeySaving(true);
    try {
      await axiosAuth.put(`/admin/api-keys/${keyName}`, { value: apiKeyDraft });
      setApiKeyMsg(prev => ({ ...prev, [keyName]: { type: 'ok', text: apiKeyDraft ? 'Saved' : 'Removed — using environment variable' } }));
      setApiKeyEditing(null);
      setApiKeyDraft('');
      loadApiKeys();
      setTimeout(() => setApiKeyMsg(prev => ({ ...prev, [keyName]: null })), 4000);
    } catch (e) {
      setApiKeyMsg(prev => ({ ...prev, [keyName]: { type: 'err', text: e?.response?.data?.detail || 'Save failed' } }));
    } finally { setApiKeySaving(false); }
  };

  const testApiKey = async (keyName) => {
    setApiKeyMsg(prev => ({ ...prev, [keyName]: { type: 'info', text: 'Testing…' } }));
    try {
      const r = await axiosAuth.post(`/admin/api-keys/${keyName}/test`);
      setApiKeyMsg(prev => ({
        ...prev,
        [keyName]: { type: r.data.status === 'ok' ? 'ok' : 'err', text: r.data.message }
      }));
      setTimeout(() => setApiKeyMsg(prev => ({ ...prev, [keyName]: null })), 5000);
    } catch (e) {
      setApiKeyMsg(prev => ({ ...prev, [keyName]: { type: 'err', text: e?.response?.data?.detail || 'Test failed' } }));
    }
  };

  // Database config state
  const [dbConfig, setDbConfig] = useState(null);
  const [dbEditing, setDbEditing] = useState(false);
  const [dbForm, setDbForm] = useState({ host: '', port: '5432', database: '', username: '', password: '' });
  const [dbSaving, setDbSaving] = useState(false);
  const [dbMsg, setDbMsg] = useState(null);

  const loadDbConfig = useCallback(() => {
    axiosAuth.get('/admin/db-config')
      .then(r => setDbConfig(r.data))
      .catch(() => {});
  }, [axiosAuth]);

  const saveDbConfig = async () => {
    setDbSaving(true);
    setDbMsg(null);
    try {
      const res = await axiosAuth.put('/admin/db-config', dbForm);
      setDbMsg({ type: 'ok', text: res.data.message || 'Saved' });
      setDbEditing(false);
      loadDbConfig();
      setTimeout(() => setDbMsg(null), 5000);
    } catch (e) {
      setDbMsg({ type: 'err', text: e?.response?.data?.detail || 'Save failed' });
    } finally { setDbSaving(false); }
  };

  const testDbConfig = async () => {
    setDbMsg({ type: 'info', text: 'Testing…' });
    try {
      const res = await axiosAuth.post('/admin/db-config/test', dbForm);
      setDbMsg({ type: res.data.status === 'ok' ? 'ok' : 'err', text: res.data.message });
      setTimeout(() => setDbMsg(null), 5000);
    } catch (e) {
      setDbMsg({ type: 'err', text: e?.response?.data?.detail || 'Test failed' });
    }
  };

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [aiOverview, setAiOverview] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [workingDirInfo, setWorkingDirInfo] = useState(null);
  const [workingDirLoading, setWorkingDirLoading] = useState(true);
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [syncLog, setSyncLog] = useState([]);
  const [syncLogError, setSyncLogError] = useState(false);
  const [syncLogLoading, setSyncLogLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configMessage, setConfigMessage] = useState(null);
  const [importingAll, setImportingAll] = useState(false);
  const [importingKeys, setImportingKeys] = useState({});

  const toggleCard = useCallback((key) => {
    setCardOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const loadOverview = useCallback(() => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    setOverviewLoading(true);
    return axiosAuth.get('/admin/activity/overview')
      .then(res => setOverview(res.data || null))
      .catch(() => setOverview(null))
      .finally(() => setOverviewLoading(false));
  }, [axiosAuth, currentUser]);

  const loadAiOverview = useCallback(() => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    setAiLoading(true);
    return axiosAuth.get('/admin/ai/overview')
      .then(res => setAiOverview(res.data || null))
      .catch(() => setAiOverview(null))
      .finally(() => setAiLoading(false));
  }, [axiosAuth, currentUser]);

  // ── AI Cost Tracking state ──────────────────────────────────────────────
  const [aiSettings, setAiSettings] = useState(null);
  const [aiSpendStatus, setAiSpendStatus] = useState(null);
  const [aiCostSummary, setAiCostSummary] = useState([]);
  const [aiCostsByInterview, setAiCostsByInterview] = useState([]);
  const [aiCostsByUser, setAiCostsByUser] = useState([]);
  // One flag for all three cost loaders — they render as one screen, and a
  // failed load showing "No AI costs logged yet" reads as "this cost nothing".
  const [aiCostsError, setAiCostsError] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [aiSettingsDraft, setAiSettingsDraft] = useState({});
  const [aiPeriodSummary, setAiPeriodSummary] = useState(null);
  const [aiCostDateFrom, setAiCostDateFrom] = useState('');
  const [aiCostDateTo, setAiCostDateTo] = useState('');
  const [aiCostPreset, setAiCostPreset] = useState('all');

  const loadAiSettings = useCallback(() => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    return axiosAuth.get('/admin/ai-settings')
      .then(res => {
        setAiSettings(res.data || {});
        const draft = {};
        Object.entries(res.data || {}).forEach(([k, v]) => { draft[k] = v.value; });
        setAiSettingsDraft(draft);
      })
      .catch(() => setAiSettings({}));
  }, [axiosAuth, currentUser]);

  const loadAiSpendStatus = useCallback(() => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    return axiosAuth.get('/admin/ai-spend-status')
      .then(res => setAiSpendStatus(res.data || null))
      .catch(() => setAiSpendStatus(null));
  }, [axiosAuth, currentUser]);

  const loadAiCostSummary = useCallback((dateFrom, dateTo) => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return axiosAuth.get('/admin/ai-costs/summary', { params })
      .then(res => { setAiCostSummary(res.data?.rows || res.data || []); setAiCostsError(false); })
      .catch(() => setAiCostsError(true));
  }, [axiosAuth, currentUser]);

  const loadAiCostsByInterview = useCallback((dateFrom, dateTo) => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return axiosAuth.get('/admin/ai-costs/by-interview', { params })
      .then(res => { setAiCostsByInterview(res.data?.interviews || res.data || []); setAiCostsError(false); })
      .catch(() => setAiCostsError(true));
  }, [axiosAuth, currentUser]);

  const loadAiCostsByUser = useCallback((dateFrom, dateTo) => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return axiosAuth.get('/admin/ai-costs/by-user', { params })
      .then(res => { setAiCostsByUser(res.data?.rows || res.data || []); setAiCostsError(false); })
      .catch(() => setAiCostsError(true));
  }, [axiosAuth, currentUser]);

  const loadAiPeriodSummary = useCallback(() => {
    if (currentUser?.role !== 'admin') return Promise.resolve();
    return axiosAuth.get('/admin/ai-costs/period-summary')
      .then(res => setAiPeriodSummary(res.data || null))
      .catch(() => setAiPeriodSummary(null));
  }, [axiosAuth, currentUser]);

  const applyAiCostDateRange = useCallback((preset, from, to) => {
    setAiCostPreset(preset);
    const now = new Date();
    let dateFrom = from || '';
    let dateTo = to || '';
    if (preset === '30d') { dateFrom = new Date(now - 30 * 86400000).toISOString().slice(0, 10); dateTo = ''; }
    else if (preset === '60d') { dateFrom = new Date(now - 60 * 86400000).toISOString().slice(0, 10); dateTo = ''; }
    else if (preset === '90d') { dateFrom = new Date(now - 90 * 86400000).toISOString().slice(0, 10); dateTo = ''; }
    else if (preset === 'month') { dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10); dateTo = ''; }
    else if (preset === 'quarter') {
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      dateFrom = qStart.toISOString().slice(0, 10); dateTo = '';
    }
    else if (preset === 'year') { dateFrom = `${now.getFullYear()}-01-01`; dateTo = ''; }
    else if (preset === 'all') { dateFrom = ''; dateTo = ''; }
    setAiCostDateFrom(dateFrom);
    setAiCostDateTo(dateTo);
    loadAiCostSummary(dateFrom, dateTo);
    loadAiCostsByInterview(dateFrom, dateTo);
    loadAiCostsByUser(dateFrom, dateTo);
  }, [loadAiCostSummary, loadAiCostsByInterview, loadAiCostsByUser]);

  const saveAiSettings = useCallback((updates) => {
    setAiSettingsSaving(true);
    return axiosAuth.patch('/admin/ai-settings', updates)
      .then(() => { loadAiSettings(); loadAiSpendStatus(); })
      .catch(err => pushToast('admin-ai-settings-save-error', 'error', 'Failed to save settings: ' + (err.response?.data?.detail || err.message)))
      .finally(() => setAiSettingsSaving(false));
  }, [axiosAuth, loadAiSettings, loadAiSpendStatus, pushToast]);

  const browseTo = useCallback((subdir = '') => {
    setBrowseLoading(true);
    return axiosAuth.get('/browse', { params: { subdir } })
      .then(res => setBrowseData(res.data || null))
      .catch(() => setBrowseData(null))
      .finally(() => setBrowseLoading(false));
  }, [axiosAuth]);

  const loadWorkingDir = useCallback(() => {
    setWorkingDirLoading(true);
    return axiosAuth.get('/working-dir')
      .then(res => {
        const next = res.data || null;
        setWorkingDirInfo(next);
        return next;
      })
      .catch(() => {
        setWorkingDirInfo(null);
        return null;
      })
      .finally(() => setWorkingDirLoading(false));
  }, [axiosAuth]);

  const loadSyncLog = useCallback(() => {
    setSyncLogLoading(true);
    return axiosAuth.get('/sync-log')
      .then(res => { setSyncLog(res.data || []); setSyncLogError(false); })
      .catch(() => setSyncLogError(true))
      .finally(() => setSyncLogLoading(false));
  }, [axiosAuth]);

  useEffect(() => {
    loadOverview();
    loadAiOverview();
    loadSyncLog();
    loadHelpMenus();
    loadWorkingDir().then(info => browseTo(info?.working_subdir || ''));
  }, [loadOverview, loadAiOverview, loadSyncLog, loadHelpMenus, loadWorkingDir, browseTo]);

  // Load AI cost data when AI tab is opened, refresh spend status every 60s
  useEffect(() => {
    if (activeMenu !== 'ai') return;
    loadAiSettings();
    loadAiSpendStatus();
    loadAiPeriodSummary();
    loadAiCostSummary(aiCostDateFrom, aiCostDateTo);
    loadAiCostsByInterview(aiCostDateFrom, aiCostDateTo);
    loadAiCostsByUser(aiCostDateFrom, aiCostDateTo);
    const interval = setInterval(loadAiSpendStatus, 60000);
    return () => clearInterval(interval);
  }, [activeMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyWorkingDirectory = useCallback((subdir) => {
    setConfigSaving(true);
    setConfigMessage(null);
    axiosAuth.post('/working-dir', { subdir })
      .then(() => loadWorkingDir())
      .then(info => {
        browseTo(info?.working_subdir || '');
        setConfigMessage({ type: 'ok', text: `Working directory set to ${(info?.display_path || '').replace(/^\/+/, '') || 'Root'}` });
        onWorkingDirectoryChanged?.(info);
      })
      .catch(err => {
        setConfigMessage({ type: 'error', text: err?.response?.data?.detail || 'Could not update working directory.' });
      })
      .finally(() => setConfigSaving(false));
  }, [axiosAuth, browseTo, loadWorkingDir, onWorkingDirectoryChanged]);

  const importPair = useCallback((pair) => {
    const key = pair.subtitle_file || `${pair.video_id}_${pair.part}`;
    setImportingKeys(prev => ({ ...prev, [key]: true }));
    axiosAuth.post('/working-dir/import', null, { params: { video_id: pair.video_id, part: pair.part } })
      .then(() => Promise.all([loadWorkingDir(), loadSyncLog(), loadOverview()]))
      .then(([info]) => {
        browseTo(info?.working_subdir || '');
        onWorkingDirectoryChanged?.(info);
      })
      .catch(() => {})
      .finally(() => setImportingKeys(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      }));
  }, [axiosAuth, browseTo, loadOverview, loadSyncLog, loadWorkingDir, onWorkingDirectoryChanged]);

  const importAll = useCallback(() => {
    setImportingAll(true);
    axiosAuth.post('/working-dir/import-all')
      .then(() => Promise.all([loadWorkingDir(), loadSyncLog(), loadOverview()]))
      .then(([info]) => {
        browseTo(info?.working_subdir || '');
        onWorkingDirectoryChanged?.(info);
      })
      .catch(() => {})
      .finally(() => setImportingAll(false));
  }, [axiosAuth, browseTo, loadOverview, loadSyncLog, loadWorkingDir, onWorkingDirectoryChanged]);

  const totals = overview?.totals || {};
  const aiTotals = aiOverview?.totals || {};
  const aiByFeature = aiOverview?.by_feature || [];
  const aiByModel = aiOverview?.by_model || [];
  const aiRecentRuns = aiOverview?.recent_runs || [];
  const [aiRunFilter, setAiRunFilter] = useState('all');
  const [aiExpandedRuns, setAiExpandedRuns] = useState({});  // { runId: true }

  const aiRunCounts = useMemo(() => {
    const c = { all: aiRecentRuns.length, success: 0, error: 0, running: 0, timeout: 0 };
    aiRecentRuns.forEach(r => {
      if (r.status === 'success') c.success++;
      else if (r.status?.includes('timeout')) c.timeout++;
      else if (r.status === 'running' || r.status === 'pending') c.running++;
      else c.error++;
    });
    return c;
  }, [aiRecentRuns]);

  const filteredAiRuns = useMemo(() => {
    if (aiRunFilter === 'all') return aiRecentRuns;
    return aiRecentRuns.filter(r => {
      if (aiRunFilter === 'success') return r.status === 'success';
      if (aiRunFilter === 'running') return r.status === 'running' || r.status === 'pending';
      if (aiRunFilter === 'timeout') return r.status?.includes('timeout');
      if (aiRunFilter === 'error') return r.status !== 'success' && !r.status?.includes('timeout') && r.status !== 'running' && r.status !== 'pending';
      return true;
    });
  }, [aiRecentRuns, aiRunFilter]);
  const users = overview?.users || [];
  const locations = overview?.locations || [];
  const activityByArea = overview?.activity_by_area || [];
  const recentActivity = overview?.recent_activity || [];
  const recentSessions = overview?.recent_sessions || [];
  const pendingImports = useMemo(
    () => (workingDirInfo?.pairs || []).filter(pair => !pair.already_imported).length,
    [workingDirInfo]
  );
  const adminSections = [
    { id: 'ai', label: 'AI', count: aiTotals.session_count || 0 },
    { id: 'api-keys', label: 'API Keys & DB', count: apiKeys.filter(k => k.is_set).length },
    // Count is folders awaiting intake, so the pill reads as a to-do rather
    // than as a total.
    { id: 'new-project', label: 'New Project', count: intakeCount },
    { id: 'statistics', label: 'Statistics', count: activityByArea.length || users.length || totals.user_count || 0 },
    { id: 'help-menus', label: 'Help Menus', count: helpMenus.length },
    { id: 'tools', label: 'Tools', count: 4 },
    { id: 'users', label: 'Users', count: users.length || accessRequests.length || 0 },
    { id: 'workspace', label: 'Workspace', count: pendingImports },
  ];

  if (currentUser?.role !== 'admin') {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', background: theme.bg }}>
        <div style={{
          width: 'min(520px, 100%)',
          padding: '32px',
          background: theme.sidebar,
          border: `1px solid ${theme.border}`,
          borderRadius: '22px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '18px',
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}>
            <Icon icon={Shield} size={24} color="#f59e0b" />
          </div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: theme.text, marginBottom: '8px' }}>Admin access required</div>
          <div style={{ fontSize: '0.88rem', color: theme.textSub, lineHeight: 1.6 }}>
            This dashboard is only available to administrators.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, color: theme.text }}>
      <div
        style={{
          width: 'min(1440px, 100%)',
          margin: '0 auto',
          padding: '16px 24px 30px',
          boxSizing: 'border-box',
          minHeight: '100%',
        }}
      >
      {/* Header — compact with stats + overview merged */}
      <div style={{
        padding: '18px 22px',
        borderRadius: '18px',
        marginBottom: '14px',
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30,41,59,0.92), rgba(15,23,42,0.98))'
          : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(241,245,249,0.95))',
        border: `1px solid ${theme.border}`,
        boxShadow: '0 14px 28px rgba(15,23,42,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <Icon icon={Settings2} size={16} color="#2563eb" />
            <span style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.01em' }}>Admin Dashboard</span>
            {workingDirInfo?.display_path && (
              <span style={{ fontSize: '0.72rem', color: theme.textSub, fontWeight: 600 }}>
                — {(workingDirInfo.display_path || '').replace(/^\/+/, '')}
              </span>
            )}
            {activityByArea[0] && (
              <span style={{ fontSize: '0.68rem', color: theme.textSub, opacity: 0.7 }}>
                • Top: {formatActionLabel(activityByArea[0].area, activityByArea[0].action)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={() => {
                setShowAdminHelp(v => {
                  if (!v && !adminHelpItems) {
                    axiosAuth.get('/admin/help-menus/admin-icons-guide')
                      .then(r => setAdminHelpItems(r.data))
                      .catch(() => {});
                  }
                  return !v;
                });
              }}
              style={{
                padding: '6px', borderRadius: '8px',
                border: `1px solid ${showAdminHelp ? '#f59e0b' : theme.border}`, background: showAdminHelp ? '#f59e0b18' : theme.sidebar,
                color: showAdminHelp ? '#f59e0b' : theme.text, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
              title="Admin help guide"
            >
              <Icon icon={HelpCircle} size={14} />
            </button>
            <Button
              theme={theme}
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={() => { loadOverview(); loadAiOverview(); }}
            >
              Refresh
            </Button>
          </div>
        </div>
        {/* Admin help panel — collapsible */}
        {showAdminHelp && (
          <div style={{ marginBottom: '14px', border: `1px solid #f59e0b44`, borderRadius: '12px', background: theme.card, padding: '14px', position: 'relative' }}>
            <button onClick={() => setShowAdminHelp(false)} style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, padding: '2px' }}>
              <Icon icon={X} size={14} />
            </button>
            <div style={{ fontWeight: 800, fontSize: '0.72rem', color: '#f59e0b', letterSpacing: '0.04em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icon icon={Shield} size={14} color="#f59e0b" />
              {adminHelpItems?.title || 'Admin Dashboard Icons'}
            </div>
            {(adminHelpItems?.items || []).length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
                {adminHelpItems.items.map((item, i) => (
                  <div key={i}>
                    <div style={{ fontWeight: 700, fontSize: '0.70rem', color: '#f59e0b', marginBottom: '4px' }}>{item.title}</div>
                    <div style={{ fontSize: '0.68rem', color: theme.textSub, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{renderAdminHelpText(item.text, theme)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.72rem', color: theme.textSub }}>No admin help content. Click "Seed Default Help Menus" in Help Menus to create it.</div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <SummaryTile theme={theme} icon={Users} label="Users" value={totals.user_count ?? 0} tint="#2563eb" onClick={() => setActiveMenu('users')} />
          <SummaryTile theme={theme} icon={Clock3} label="Session Hours" value={formatDuration(totals.total_session_seconds)} tint="#059669" onAction={runSessionCleanup} actionTitle="Fix inflated session hours" onClick={() => setActiveMenu('statistics')} />
          <SummaryTile theme={theme} icon={Activity} label="Tracked Work" value={formatDuration(totals.total_activity_seconds)} tint="#7c3aed" onAction={runSessionCleanup} actionTitle="Fix inflated tracked hours" onClick={() => setActiveMenu('statistics')} />
          <SummaryTile theme={theme} icon={Bot} label="AI Sessions" value={aiTotals.session_count ?? 0} tint="#7c3aed" onClick={() => setActiveMenu('ai')} />
          <SummaryTile theme={theme} icon={Database} label="AI Cost" value={formatUsd(aiTotals.estimated_cost_usd)} tint="#0f766e" onClick={() => setActiveMenu('ai')} />
          <SummaryTile theme={theme} icon={Globe} label="Locations" value={totals.location_count ?? 0} tint="#d97706" onClick={() => setActiveMenu('statistics')} />
          {accessRequests.length > 0 && <SummaryTile theme={theme} icon={Mail} label="Pending Requests" value={accessRequests.length} tint="#dc2626" onClick={() => setActiveMenu('users')} />}
          {pendingImports > 0 && <SummaryTile theme={theme} icon={Database} label="Pending Imports" value={pendingImports} tint="#dc2626" onClick={() => setActiveMenu('workspace')} />}
        </div>
      </div>

      {/* System Status Bar — live service health */}
      {systemStatus && (
        <div style={{
          display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center',
          padding: '8px 14px', marginBottom: '10px', borderRadius: '12px',
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
          border: `1px solid ${theme.border}`,
          fontSize: '0.68rem',
        }}>
          <span style={{ fontWeight: 700, color: theme.textSub, marginRight: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Services
          </span>
          {Object.entries(systemStatus).map(([key, svc]) => {
            const isOk = svc.status === 'ok';
            const isWarn = svc.status === 'warning';
            const isErr = svc.status === 'error' || svc.status === 'not_configured';
            const dotColor = isOk ? '#22c55e' : isWarn ? '#f59e0b' : '#ef4444';
            let detail = '';
            if (svc.latency_ms) detail += `${svc.latency_ms}ms`;
            if (svc.count !== undefined) detail = `${svc.count}`;
            if (svc.free_gb !== undefined) detail = `${svc.free_gb}GB free (${svc.used_pct}%)`;
            if (svc.model) detail += detail ? ` · ${svc.model}` : svc.model;
            if (svc.ffmpeg) detail += detail ? ` · ffmpeg` : `ffmpeg: ${svc.ffmpeg}`;
            if (svc.error && isErr) detail = svc.status === 'not_configured' ? 'not configured' : 'offline';
            return (
              <span key={key} title={svc.error || svc.url || ''}
                aria-label={`${svc.label}: ${svc.status}${svc.error ? ' — ' + svc.error : ''}`}
                style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '3px 8px', borderRadius: '6px',
                background: isOk ? 'rgba(34,197,94,0.08)' : isWarn ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${isOk ? 'rgba(34,197,94,0.2)' : isWarn ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}`,
                color: isOk ? (isDarkMode ? '#86efac' : '#15803d') : isWarn ? '#f59e0b' : '#ef4444',
                fontWeight: 600,
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                {svc.label}
                {detail && <span style={{ fontWeight: 400, opacity: 0.8 }}>{detail}</span>}
              </span>
            );
          })}
          <button onClick={fetchSystemStatus} disabled={statusLoading} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSub, marginLeft: 'auto',
          }} title="Refresh status">
            <Icon icon={RefreshCw} size={11} style={{ animation: statusLoading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      )}

      {/* Background Jobs — collapsible, above section pills */}
      <BackgroundJobsInline theme={theme} axiosAuth={axiosAuth} />

      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        marginBottom: '10px',
        padding: '6px 0 2px',
        background: theme.bg,
      }}>
        <div style={{
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap',
          padding: '4px',
          borderRadius: '12px',
          background: theme.sidebar,
          border: `1px solid ${theme.border}`,
        }}>
          {adminSections.slice().sort((a, b) => a.label.localeCompare(b.label)).map(section => (
            <button
              key={section.id}
              onClick={() => setActiveMenu(section.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: activeMenu === section.id ? '#2563eb' : 'transparent',
                color: activeMenu === section.id ? '#fff' : theme.text,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>{section.label}</span>
              <span style={{
                padding: '1px 6px',
                borderRadius: '999px',
                background: activeMenu === section.id ? 'rgba(255,255,255,0.2)' : theme.card,
                color: activeMenu === section.id ? '#fff' : theme.textSub,
                fontSize: '0.62rem',
                fontWeight: 700,
              }}>
                {section.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>


        {activeMenu === 'statistics' && (
          <>
            <AdminCard
              theme={theme}
              title="User Tracking"
              icon={Users}
              accent="#059669"
              isOpen={cardOpen.tracking}
              onToggle={() => toggleCard('tracking')}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                      {['User', 'Role', 'Logged Time', 'Tracked Work', 'Sessions', 'Last Seen', 'Location', 'Top Activity'].map(label => (
                        <th key={label} style={{ textAlign: 'left', padding: '10px 8px', color: theme.textSub, fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '12px 8px', minWidth: '180px' }}>
                          <div style={{ fontWeight: 700, color: theme.text }}>{user.display_name}</div>
                          <div style={{ fontSize: '0.75rem', color: theme.textSub }}>{user.email}</div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '999px',
                            background: user.role === 'admin' ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.12)',
                            border: `1px solid ${user.role === 'admin' ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.3)'}`,
                            color: user.role === 'admin' ? '#d97706' : '#2563eb',
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                          }}>
                            {user.role}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: 700 }}>{formatDuration(user.session_seconds)}</td>
                        <td style={{ padding: '12px 8px', fontWeight: 700 }}>{formatDuration(user.tracked_seconds)}</td>
                        <td style={{ padding: '12px 8px' }}>{user.session_count}</td>
                        <td style={{ padding: '12px 8px', color: theme.textSub }}>{formatDateTime(user.last_seen_at)}</td>
                        <td style={{ padding: '12px 8px', color: theme.textSub }}>{user.last_location || 'Unknown'}</td>
                        <td style={{ padding: '12px 8px', color: theme.textSub }}>
                          {user.top_activities?.[0] ? formatActionLabel(user.top_activities[0].area, user.top_activities[0].action) : '—'}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ padding: '20px 8px', color: theme.textSub, textAlign: 'center' }}>
                          No tracked users yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Cleanup inflated sessions */}
              <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={runSessionCleanup}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', fontSize: '0.68rem', fontWeight: 700,
                    borderRadius: '8px', cursor: 'pointer',
                    color: '#0f766e', background: '#0f766e14', border: '1px solid #0f766e33',
                  }}
                >
                  <Icon icon={RefreshCw} size={12} color="#0f766e" />
                  Clean Up Inflated Hours
                </button>
              </div>
            </AdminCard>

            {/* ── Locations Card ── */}
            {locations.length > 0 && (() => {
              const TOP_COUNT = 10;
              return (
                <AdminCard
                  theme={theme}
                  title={`Locations (${locations.length})`}
                  icon={MapPin}
                  accent="#d97706"
                  isOpen={cardOpen.locations}
                  onToggle={() => toggleCard('locations')}
                >
                  {(() => {
                    const displayLocs = showAllLocations ? locations : locations.slice(0, TOP_COUNT);
                    return (
                      <>
                        {/* Filter pills */}
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                          <button
                            onClick={() => setShowAllLocations(false)}
                            style={{
                              padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '10px', border: 'none', cursor: 'pointer',
                              background: !showAllLocations ? '#d97706' : theme.card, color: !showAllLocations ? '#fff' : theme.textSub,
                            }}
                          >Top {Math.min(TOP_COUNT, locations.length)}</button>
                          {locations.length > TOP_COUNT && (
                            <button
                              onClick={() => setShowAllLocations(true)}
                              style={{
                                padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '10px', border: 'none', cursor: 'pointer',
                                background: showAllLocations ? '#d97706' : theme.card, color: showAllLocations ? '#fff' : theme.textSub,
                              }}
                            >All ({locations.length})</button>
                          )}
                        </div>

                        {/* Location list */}
                        <div style={{ maxHeight: showAllLocations ? '400px' : 'none', overflowY: showAllLocations ? 'auto' : 'visible' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
                                {['#', 'Location', 'Time', 'Sessions', 'Users'].map(h => (
                                  <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Location' ? 'left' : 'center', fontWeight: 800, fontSize: '0.62rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textSub }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {displayLocs.map((loc, i) => (
                                <tr key={loc.location_label} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? 'transparent' : theme.card }}>
                                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: theme.textSub, fontSize: '0.62rem' }}>{i + 1}</td>
                                  <td style={{ padding: '8px', fontWeight: 600, color: theme.text }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <Icon icon={MapPin} size={11} color="#d97706" />
                                      {loc.location_label}
                                    </div>
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 700, color: '#d97706' }}>{formatDuration(loc.total_seconds)}</td>
                                  <td style={{ padding: '8px', textAlign: 'center', color: theme.textSub }}>{loc.session_count}</td>
                                  <td style={{ padding: '8px', textAlign: 'center', color: theme.textSub }}>{loc.user_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()}
                </AdminCard>
              );
            })()}

            <AdminCard
              theme={theme}
              title="Activity Timing"
              icon={Activity}
              accent="#7c3aed"
              isOpen={cardOpen.activity}
              onToggle={() => toggleCard('activity')}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {activityByArea.map(item => (
                  <div key={`${item.area}_${item.action || 'none'}`} style={{
                    padding: '14px 16px',
                    borderRadius: '16px',
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                  }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '8px' }}>
                      {item.area}
                    </div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 800, color: theme.text, marginBottom: '6px' }}>
                      {item.action || 'General'}
                    </div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#7c3aed', marginBottom: '4px' }}>
                      {formatDuration(item.total_seconds)}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: theme.textSub }}>
                      {item.segment_count} segments • {item.user_count} users
                    </div>
                  </div>
                ))}
                {activityByArea.length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.84rem' }}>No tracked activity yet.</div>
                )}
              </div>
            </AdminCard>

            <AdminCard
              theme={theme}
              title="Logs"
              icon={Database}
              accent="#d97706"
              isOpen={cardOpen.logs}
              onToggle={() => toggleCard('logs')}
              extra={syncLogLoading ? <Icon icon={Loader} size={15} color={theme.textSub} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub }}>
                  Recent Activity Feed
                </div>
                <button onClick={loadOverview} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: theme.textSub }}>
                  <Icon icon={RefreshCw} size={14} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {recentActivity.slice(0, 18).map(item => (
                  <div key={item.id} style={{ padding: '12px 13px', borderRadius: '14px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '5px' }}>
                      <div style={{ fontWeight: 700, color: theme.text }}>{item.user_name}</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textSub }}>{formatDuration(item.duration_seconds)}</div>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: theme.textSub, marginBottom: '4px' }}>
                      {formatActionLabel(item.area, item.action)}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: theme.textSub, lineHeight: 1.5 }}>
                      {item.project_root ? `${item.project_root}` : 'No project'}{item.video_id ? ` • ${item.video_id}` : ''}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: theme.textSub, marginTop: '6px' }}>
                      {formatDateTime(item.started_at)}
                    </div>
                  </div>
                ))}
                {recentActivity.length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No recent activity segments yet.</div>
                )}
              </div>
            </div>

            <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub }}>
                  Sync Log
                </div>
                <button onClick={loadSyncLog} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: theme.textSub }}>
                  <Icon icon={RefreshCw} size={14} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {syncLog.slice(0, 24).map((row, idx) => (
                  <div key={`${row.video_id}_${row.part_number}_${idx}`} style={{ padding: '11px 12px', borderRadius: '12px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                    <div style={{ fontWeight: 700, color: theme.text, fontSize: '0.8rem', marginBottom: '3px' }}>{row.video_id}</div>
                    <div style={{ fontSize: '0.74rem', color: theme.textSub, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Icon icon={Film} size={10} color={theme.textSub} /> Tape {row.part_number} • {row.line_count} cues
                    </div>
                    <div style={{ fontSize: '0.72rem', color: theme.textSub }}>{formatDateTime(row.synced_at)}</div>
                  </div>
                ))}
                {syncLogError && !syncLogLoading && (
                  <LoadFailure theme={theme} what="the sync log" onRetry={loadSyncLog} compact />
                )}
                {syncLog.length === 0 && !syncLogLoading && !syncLogError && (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No sync activity yet.</div>
                )}
              </div>
            </div>

            <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '12px' }}>
                Recent Sessions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {recentSessions.slice(0, 18).map(item => (
                  <div key={item.id} style={{ padding: '11px 12px', borderRadius: '12px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                      <div style={{ fontWeight: 700, color: theme.text }}>{item.user_name}</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textSub }}>{formatDuration(item.duration_seconds)}</div>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: theme.textSub }}>
                      {item.location_label || 'Unknown location'}{item.device_name ? ` • ${item.device_name}` : ''}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: theme.textSub, marginTop: '5px' }}>
                      {formatDateTime(item.started_at)}
                    </div>
                  </div>
                ))}
                {recentSessions.length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No recent sessions yet.</div>
                )}
              </div>
            </div>
              </div>
            </AdminCard>
          </>
        )}

        {activeMenu === 'ai' && (
          <>
            <AdminCard
              theme={theme}
              title="AI Usage and Cost"
              icon={Bot}
              accent="#7c3aed"
              isOpen={cardOpen.ai}
              onToggle={() => toggleCard('ai')}
              extra={aiLoading ? <Icon icon={Loader} size={15} color={theme.textSub} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {[
                  { label: 'Sessions', value: formatNumber(aiTotals.session_count), tint: '#2563eb' },
                  { label: 'Runs / Batches', value: formatNumber(aiTotals.run_count), tint: '#7c3aed' },
                  { label: 'Success', value: formatNumber(aiTotals.success_count), tint: '#059669' },
                  { label: 'Errors', value: formatNumber(aiTotals.error_count), tint: '#dc2626' },
                  { label: 'Total Tokens', value: formatNumber(aiTotals.total_tokens), tint: '#d97706' },
                  { label: 'Estimated Cost', value: formatUsd(aiTotals.estimated_cost_usd), tint: '#0f766e' },
                ].map(item => (
                  <div key={item.label} style={{ padding: '14px 16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '8px' }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: item.tint }}>{item.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '12px' }}>
                    By Feature
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {aiByFeature.map(item => (
                      <div key={item.feature} style={{ padding: '12px 13px', borderRadius: '12px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
                          <div style={{ fontWeight: 800, color: theme.text }}>{item.feature}</div>
                          <div style={{ fontSize: '0.74rem', color: theme.textSub }}>{formatUsd(item.estimated_cost_usd)}</div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: theme.textSub }}>
                          {formatNumber(item.session_count)} sessions • {formatNumber(item.run_count)} runs{item.total_tokens > 0 ? ` • ${formatNumber(item.total_tokens)} tokens` : ''}{item.success_count != null ? ` • ${item.success_count} success` : ''}
                        </div>
                      </div>
                    ))}
                    {aiByFeature.length === 0 && (
                      <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No AI traces recorded yet.</div>
                    )}
                  </div>
                </div>

                <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '12px' }}>
                    By Model
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {aiByModel.map(item => (
                      <div key={item.model} style={{ padding: '12px 13px', borderRadius: '12px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
                          <div style={{ fontWeight: 800, color: theme.text }}>{item.model}</div>
                          <div style={{ fontSize: '0.74rem', color: theme.textSub }}>{formatUsd(item.estimated_cost_usd)}</div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: theme.textSub }}>
                          {formatNumber(item.run_count)} runs • {formatNumber(item.total_tokens)} tokens
                        </div>
                      </div>
                    ))}
                    {aiByModel.length === 0 && (
                      <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No model usage yet.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Per-subject cost breakdown */}
              {(aiOverview?.by_subject || []).length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '12px' }}>
                    Cost by Subject
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(aiOverview?.by_subject || []).map(item => (
                      <div key={item.subject} style={{ padding: '12px 14px', borderRadius: '12px', background: theme.card, border: `1px solid ${theme.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
                          <div style={{ fontWeight: 800, color: theme.text }}>{item.subject.replace(/_/g, ' ')}</div>
                          <div style={{ fontWeight: 800, color: '#0f766e' }}>{formatUsd(item.total_cost)}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.72rem', color: theme.textSub }}>
                          {item.qa_cost > 0 && <span>QA: <strong style={{ color: '#2563eb' }}>{formatUsd(item.qa_cost)}</strong></span>}
                          {item.seed_cost > 0 && <span>Seed: <strong style={{ color: '#7c3aed' }}>{formatUsd(item.seed_cost)}</strong></span>}
                          {item.whisper_cost > 0 && <span>Whisper: <strong style={{ color: '#d97706' }}>{formatUsd(item.whisper_cost)}</strong></span>}
                          {item.translation_cost > 0 && <span>Translation: <strong style={{ color: '#059669' }}>{formatUsd(item.translation_cost)}</strong></span>}
                          {item.clips_cost > 0 && <span>Clips: <strong style={{ color: '#dc2626' }}>{formatUsd(item.clips_cost)}</strong></span>}
                          {item.whisper_duration_ms > 0 && <span>Whisper time: <strong>{formatDurationMs(item.whisper_duration_ms)}</strong></span>}
                          <span>{item.run_count} runs • {formatNumber(item.total_tokens)} tokens</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transcription stats */}
              {(() => {
                const ts = aiOverview?.transcription_stats;
                if (!ts || ts.total_runs === 0) return null;
                return (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '12px' }}>
                      Whisper Transcription
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                      {[
                        { label: 'Total Runs', value: ts.total_runs, tint: '#7c3aed' },
                        { label: 'Success', value: ts.success_count, tint: '#059669' },
                        { label: 'Errors', value: ts.error_count, tint: ts.error_count > 0 ? '#dc2626' : '#64748b' },
                        { label: 'Total Time', value: formatDurationMs(ts.total_duration_ms), tint: '#2563eb' },
                        { label: 'Avg per Tape', value: formatDurationMs(ts.avg_duration_ms), tint: '#d97706' },
                        { label: 'Projects', value: ts.unique_projects, tint: '#0f766e' },
                        { label: 'Videos', value: ts.unique_videos, tint: '#64748b' },
                      ].map(item => (
                        <div key={item.label} style={{ padding: '12px 14px', borderRadius: '14px', background: theme.card, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '6px' }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: item.tint }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ marginTop: '14px', fontSize: '0.76rem', color: theme.textSub }}>
                {aiOverview?.trace_store_full
                  ? 'Full AI payload capture is enabled for trace rows.'
                  : 'Trace rows currently store previews and usage metadata; full payload storage is disabled.'}
              </div>
            </AdminCard>

            {/* ── AI Cost Controls ─────────────────────────────────────── */}
            <AdminCard
              theme={theme}
              title="AI Cost Controls"
              icon={DollarSign}
              accent="#0f766e"
              isOpen={cardOpen.aiCostControls}
              onToggle={() => toggleCard('aiCostControls')}
            >
              {/* Spend Status Gauges */}
              {aiSpendStatus && !aiSpendStatus.error && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                    Live Spend Status
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                    {[
                      { label: 'Today', spent: aiSpendStatus.daily_spent_usd, limit: aiSpendStatus.daily_limit_usd, pct: aiSpendStatus.daily_pct },
                      { label: 'This Month', spent: aiSpendStatus.monthly_spent_usd, limit: aiSpendStatus.monthly_limit_usd, pct: aiSpendStatus.monthly_pct },
                    ].map(g => {
                      const pct = Math.min(g.pct * 100, 100);
                      const overAlert = g.pct >= (aiSpendStatus.alert_threshold_pct || 80) / 100;
                      const overLimit = g.pct >= 1.0;
                      const barColor = overLimit ? '#dc2626' : overAlert ? '#f59e0b' : '#0f766e';
                      return (
                        <div key={g.label} style={{ padding: '14px 16px', borderRadius: '14px', background: theme.card, border: `1px solid ${overLimit ? '#dc2626' : theme.border}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{g.label}</div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: barColor }}>
                              {formatUsdFine(g.spent)} / {formatUsdFine(g.limit)}
                            </div>
                          </div>
                          <div style={{ width: '100%', height: '10px', borderRadius: '5px', background: theme.sidebar, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 0.4s ease' }} />
                          </div>
                          <div style={{ fontSize: '0.7rem', color: theme.textSub, marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                            <span>{(g.pct * 100).toFixed(1)}% used</span>
                            {overLimit && <span style={{ color: '#dc2626', fontWeight: 700 }}><Icon icon={AlertTriangle} size={11} /> HARD CAP HIT</span>}
                            {!overLimit && overAlert && <span style={{ color: '#f59e0b', fontWeight: 700 }}>Above alert threshold</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Settings form */}
              {aiSettings && (
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                    Limits & Pricing
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                    {[
                      { key: 'daily_limit_usd', label: 'Daily Limit (USD)', type: 'number', step: '0.50' },
                      { key: 'monthly_limit_usd', label: 'Monthly Limit (USD)', type: 'number', step: '5.00' },
                      { key: 'alert_threshold_pct', label: 'Alert Threshold (%)', type: 'number', step: '5', min: 50, max: 100 },
                      { key: 'max_ai_retries', label: 'Max AI Retries', type: 'number', step: '1', min: 1, max: 5 },
                      { key: 'claude_input_cost_per_million', label: 'Claude Input ($/1M tokens)', type: 'number', step: '0.10' },
                      { key: 'claude_output_cost_per_million', label: 'Claude Output ($/1M tokens)', type: 'number', step: '0.10' },
                      { key: 'gemini_input_cost_per_million', label: 'Gemini Input ($/1M tokens)', type: 'number', step: '0.01' },
                      { key: 'gemini_output_cost_per_million', label: 'Gemini Output ($/1M tokens)', type: 'number', step: '0.01' },
                    ].map(field => (
                      <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textSub }}>{field.label}</label>
                        <Input
                          theme={theme}
                          type={field.type}
                          step={field.step}
                          min={field.min}
                          max={field.max}
                          value={aiSettingsDraft[field.key] || ''}
                          onChange={e => setAiSettingsDraft(p => ({ ...p, [field.key]: e.target.value }))}
                          style={{ fontFamily: 'monospace' }}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Hard cap toggle */}
                  <div style={{ marginTop: '14px', padding: '12px 14px', background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: theme.text, fontSize: '0.85rem' }}>Hard Cap Enabled</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textSub, marginTop: '2px' }}>
                        When enabled, AI calls are blocked (HTTP 429) once daily limit is reached. Disable to alert only.
                      </div>
                    </div>
                    <button
                      onClick={() => setAiSettingsDraft(p => ({ ...p, hard_cap_enabled: p.hard_cap_enabled === 'true' ? 'false' : 'true' }))}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        background: aiSettingsDraft.hard_cap_enabled === 'true' ? '#0f766e' : '#94a3b8',
                        color: '#fff',
                      }}
                    >
                      {aiSettingsDraft.hard_cap_enabled === 'true' ? 'ENABLED' : 'DISABLED'}
                    </button>
                  </div>

                  {/* Save button */}
                  <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      onClick={() => saveAiSettings(aiSettingsDraft)}
                      disabled={aiSettingsSaving}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '10px',
                        border: 'none',
                        background: '#0f766e',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        cursor: aiSettingsSaving ? 'wait' : 'pointer',
                      }}
                    >
                      <Icon icon={Save} size={13} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      {aiSettingsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>

                  {/* NotebookLM note */}
                  {aiSettings.notebooklm_cost_note && (
                    <div style={{ marginTop: '14px', padding: '12px 14px', background: 'rgba(245,158,11,0.08)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.3)', fontSize: '0.78rem', color: theme.text }}>
                      <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon icon={AlertTriangle} size={13} color="#f59e0b" /> NotebookLM Note
                      </div>
                      {aiSettings.notebooklm_cost_note.value}
                    </div>
                  )}
                </div>
              )}
            </AdminCard>

            {/* ── AI Cost Breakdown ────────────────────────────────────── */}
            <AdminCard
              theme={theme}
              title="AI Cost Breakdown"
              icon={TrendingUp}
              accent="#7c3aed"
              isOpen={cardOpen.aiCostBreakdown}
              onToggle={() => toggleCard('aiCostBreakdown')}
              extra={
                <a
                  href={`${BACKEND_URL}/admin/ai-costs/export.csv?token=${sessionStorage.getItem('gwv_token') || ''}${aiCostDateFrom ? `&date_from=${aiCostDateFrom}` : ''}${aiCostDateTo ? `&date_to=${aiCostDateTo}` : ''}`}
                  onClick={e => e.stopPropagation()}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '8px',
                    background: '#7c3aed',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icon icon={Download} size={11} /> CSV
                </a>
              }
            >
              {/* ── Period Summary ── */}
              {aiPeriodSummary && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                    Spend Overview
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px' }}>
                    {[
                      { label: 'Last 30 days', val: aiPeriodSummary.last_30_days },
                      { label: 'Last 60 days', val: aiPeriodSummary.last_60_days },
                      { label: 'Last 90 days', val: aiPeriodSummary.last_90_days },
                      { label: 'This month', val: aiPeriodSummary.current_month },
                      { label: 'This quarter', val: aiPeriodSummary.current_quarter },
                      { label: 'This year', val: aiPeriodSummary.current_year },
                      { label: 'All time', val: aiPeriodSummary.all_time },
                    ].map(p => (
                      <div key={p.label} style={{ padding: '8px 10px', borderRadius: '8px', background: isDarkMode ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.04)', border: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: '0.62rem', color: theme.textSub, marginBottom: '2px' }}>{p.label}</div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f766e', fontFamily: 'monospace' }}>{formatUsdFine(p.val)}</div>
                      </div>
                    ))}
                  </div>
                  {aiPeriodSummary.total_calls > 0 && (
                    <div style={{ fontSize: '0.62rem', color: theme.textSub, marginTop: '6px' }}>
                      {aiPeriodSummary.total_calls.toLocaleString()} total calls
                      {aiPeriodSummary.earliest_record && ` · since ${aiPeriodSummary.earliest_record.slice(0, 10)}`}
                    </div>
                  )}
                </div>
              )}

              {/* ── Date Range Picker ── */}
              <div style={{ marginBottom: '16px', padding: '10px 12px', borderRadius: '10px', background: isDarkMode ? 'rgba(124,58,237,0.06)' : 'rgba(124,58,237,0.03)', border: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {[
                    { key: '30d', label: '30 days' },
                    { key: '60d', label: '60 days' },
                    { key: '90d', label: '90 days' },
                    { key: 'month', label: 'This Month' },
                    { key: 'quarter', label: 'This Quarter' },
                    { key: 'year', label: 'This Year' },
                    { key: 'all', label: 'All Time' },
                  ].map(p => (
                    <button key={p.key} onClick={() => applyAiCostDateRange(p.key)}
                      style={{
                        padding: '4px 10px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                        background: aiCostPreset === p.key ? '#7c3aed' : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                        color: aiCostPreset === p.key ? '#fff' : theme.textSub,
                        border: aiCostPreset === p.key ? '1px solid #7c3aed' : `1px solid ${theme.border}`,
                      }}
                    >{p.label}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem' }}>
                  <span style={{ color: theme.textSub }}>Custom:</span>
                  <input type="date" value={aiCostDateFrom} onChange={e => { setAiCostDateFrom(e.target.value); setAiCostPreset('custom'); }}
                    style={{ padding: '3px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.7rem' }} />
                  <span style={{ color: theme.textSub }}>to</span>
                  <input type="date" value={aiCostDateTo} onChange={e => { setAiCostDateTo(e.target.value); setAiCostPreset('custom'); }}
                    style={{ padding: '3px 6px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.7rem' }} />
                  <button onClick={() => applyAiCostDateRange('custom', aiCostDateFrom, aiCostDateTo)}
                    style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    Apply
                  </button>
                </div>
              </div>

              {/* By Operation */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                  By Operation{aiCostPreset === 'all' ? '' : ` (${aiCostPreset === 'custom' ? 'Custom Range' : aiCostPreset})`}
                </div>
                {aiCostsError ? (
                  <LoadFailure theme={theme} what="AI cost data" onRetry={loadAiCostSummary} compact />
                ) : aiCostSummary.length === 0 ? (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No AI costs logged yet.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.border}`, color: theme.textSub }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Operation</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Model</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Calls</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Input</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Output</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiCostSummary.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                          <td style={{ padding: '6px 8px', fontWeight: 700, color: theme.text }}>{r.operation}</td>
                          <td style={{ padding: '6px 8px', color: theme.textSub, fontFamily: 'monospace', fontSize: '0.7rem' }}>{r.model}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.text }}>{r.calls}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.textSub }}>{(r.total_input_tokens || 0).toLocaleString()}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.textSub }}>{(r.total_output_tokens || 0).toLocaleString()}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#0f766e' }}>{formatUsdFine(r.total_cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* By Interview */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                  By Interview
                </div>
                {aiCostsError ? (
                  <LoadFailure theme={theme} what="interview cost data" onRetry={loadAiCostsByInterview} compact />
                ) : aiCostsByInterview.length === 0 ? (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No interview costs logged yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {aiCostsByInterview.map((iv, i) => (
                      <div key={i} style={{ padding: '12px 14px', borderRadius: '10px', background: theme.card, border: `1px solid ${theme.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <div style={{ fontWeight: 800, color: theme.text }}>{(iv.project_root || '(general)').replace(/_/g, ' ')}</div>
                          <div style={{ fontWeight: 800, color: '#0f766e', fontSize: '0.95rem' }}>{formatUsdFine(iv.total_cost_usd)}</div>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: theme.textSub }}>
                          {iv.total_calls} call{iv.total_calls === 1 ? '' : 's'} across {iv.operations.length} operation{iv.operations.length === 1 ? '' : 's'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* By User */}
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '10px' }}>
                  By User{aiCostPreset === 'all' ? '' : ` (${aiCostPreset === 'custom' ? 'Custom Range' : aiCostPreset})`}
                </div>
                {aiCostsError ? (
                  <LoadFailure theme={theme} what="per-user cost data" onRetry={loadAiCostsByUser} compact />
                ) : aiCostsByUser.length === 0 ? (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No user costs logged yet.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.border}`, color: theme.textSub }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>User</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Calls</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiCostsByUser.map((u, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                          <td style={{ padding: '6px 8px', color: theme.text }}>{u.user_id ? `#${u.user_id}: ${u.user_name || u.user_email || 'Unknown'}` : '(unknown)'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.text }}>{u.calls}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#0f766e' }}>{formatUsdFine(u.total_cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </AdminCard>

            <AdminCard
              theme={theme}
              title="Recent AI Runs"
              icon={Database}
              accent="#0f766e"
              isOpen={cardOpen.aiRuns}
              onToggle={() => toggleCard('aiRuns')}
              extra={aiRunCounts.running > 0 ? (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!await confirm({
                      message: `Cancel all ${aiRunCounts.running} running AI traces?`,
                      title: 'Clear running AI traces?',
                      confirmLabel: 'Clear',
                      destructive: true,
                    })) return;
                    axiosAuth.post('/admin/clear-running-ai')
                      .then(r => { pushToast('admin-clear-running-ai', 'success', `Cleared ${r.data.cleared} running traces.`); loadAiOverview(); })
                      .catch(err => pushToast('admin-clear-running-ai', 'error', 'Failed: ' + (err.response?.data?.detail || err.message)));
                  }}
                  style={{ padding: '3px 8px', fontSize: '0.58rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
                    color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433' }}
                >
                  Clear {aiRunCounts.running} Running
                </button>
              ) : null}
            >
              {/* Status filter bar */}
              <div style={{ display: 'flex', background: theme.card, borderRadius: '8px', padding: '2px', border: `1px solid ${theme.border}`, marginBottom: '14px', gap: '2px', flexWrap: 'wrap' }}>
                {[
                  { id: 'all', label: 'ALL', color: null },
                  { id: 'success', label: 'SUCCESS', color: '#166534' },
                  { id: 'error', label: 'ERRORS', color: '#b91c1c' },
                  { id: 'timeout', label: 'TIMEOUT', color: '#b45309' },
                  { id: 'running', label: 'RUNNING', color: '#2563eb' },
                ].map(f => (
                  <button key={f.id} onClick={() => setAiRunFilter(f.id)}
                    style={{
                      flex: 1, padding: '5px 8px', fontSize: '0.68rem', fontWeight: 'bold', letterSpacing: '0.03em',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', transition: '0.15s', whiteSpace: 'nowrap',
                      background: aiRunFilter === f.id ? (f.color || theme.glossaryAccent) : 'transparent',
                      color: aiRunFilter === f.id ? '#fff' : (f.color && aiRunCounts[f.id] > 0 ? f.color : theme.textSub),
                    }}>
                    {f.label}{aiRunCounts[f.id] > 0 ? ` (${aiRunCounts[f.id]})` : ''}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredAiRuns.map(run => {
                  const statusColor = run.status === 'success'
                    ? '#166534'
                    : run.status === 'running' || run.status === 'pending'
                      ? '#2563eb'
                      : run.status?.includes('timeout')
                        ? '#b45309'
                        : '#b91c1c';
                  const isExpanded = aiExpandedRuns[run.id];
                  const promptText = run.prompt_preview || '';
                  const truncatedPrompt = promptText.length > 200 ? promptText.slice(0, 200) + '…' : promptText;
                  const hasError = run.error_detail;
                  return (
                    <div key={run.id} style={{
                      padding: '15px 16px', borderRadius: '16px', background: theme.card,
                      border: `1px solid ${hasError ? '#b91c1c33' : theme.border}`,
                    }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '5px' }}>
                            <span style={{ fontWeight: 800, color: theme.text }}>{run.feature}</span>
                            {run.batch_label && (
                              <span style={{ fontSize: '0.72rem', color: theme.textSub }}>{run.batch_label}</span>
                            )}
                            <span style={{
                              padding: '3px 8px', borderRadius: '999px',
                              background: `${statusColor}14`, border: `1px solid ${statusColor}33`,
                              color: statusColor, fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase',
                            }}>
                              {run.status}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.78rem', color: theme.textSub, lineHeight: 1.5 }}>
                            {run.project_root || 'No project'}{run.video_id ? ` • ${run.video_id}` : ''}{run.part_number ? ` • Tape ${run.part_number}` : ''}
                            {' • '}{run.model || '—'} • {formatDateTime(run.started_at)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 800, color: theme.text }}>{formatUsd(run.estimated_cost_usd)}</div>
                            <div style={{ fontSize: '0.74rem', color: theme.textSub }}>
                              {run.total_tokens > 0 ? `${formatNumber(run.total_tokens)} tok • ` : ''}{run.duration_ms ? formatDurationMs(run.duration_ms) : '—'}
                            </div>
                          </div>
                          {(run.status === 'running' || run.status === 'pending') && (
                            <button onClick={() => {
                              axiosAuth.post(`/admin/cancel-ai-run/${run.id}`)
                                .then(() => loadAiOverview())
                                .catch(err => pushToast('admin-cancel-ai-run-error', 'error', 'Cancel failed: ' + (err.response?.data?.detail || err.message)));
                            }}
                              style={{ background: '#ef444414', border: '1px solid #ef444433', borderRadius: '6px',
                                       padding: '3px 7px', cursor: 'pointer', color: '#ef4444', fontSize: '0.65rem', fontWeight: 700 }}>
                              Stop
                            </button>
                          )}
                          <button onClick={() => setAiExpandedRuns(prev => ({ ...prev, [run.id]: !prev[run.id] }))}
                            style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '6px',
                                     padding: '3px 7px', cursor: 'pointer', color: theme.textSub, fontSize: '0.65rem', fontWeight: 700 }}>
                            {isExpanded ? 'Less' : 'More'}
                          </button>
                        </div>
                      </div>

                      {/* Error detail — always shown prominently */}
                      {hasError && (
                        <div style={{
                          padding: '10px 12px', borderRadius: '10px', marginBottom: '8px',
                          background: '#b91c1c0a', border: '1px solid #b91c1c22',
                          fontSize: '0.76rem', color: '#ef4444', lineHeight: 1.5, whiteSpace: 'pre-wrap',
                        }}>
                          <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', marginBottom: '4px', opacity: 0.8 }}>ERROR</div>
                          {run.error_detail}
                        </div>
                      )}

                      {/* Response preview — always shown if success */}
                      {!hasError && run.response_preview && (
                        <div style={{
                          padding: '10px 12px', borderRadius: '10px', marginBottom: '8px',
                          background: theme.sidebar, border: `1px solid ${theme.border}`,
                          fontSize: '0.74rem', color: theme.textSub, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                          maxHeight: isExpanded ? 'none' : '80px', overflow: 'hidden',
                        }}>
                          <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', color: theme.textSub, marginBottom: '4px' }}>RESPONSE</div>
                          {run.response_preview}
                        </div>
                      )}

                      {/* Expanded details */}
                      {isExpanded && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                            {[
                              ['Actor', run.actor_name || 'System'],
                              ['Input', formatNumber(run.input_tokens)],
                              ['Output', formatNumber(run.output_tokens)],
                              ['Cache Write', formatNumber(run.cache_creation_input_tokens)],
                              ['Cache Read', formatNumber(run.cache_read_input_tokens)],
                              ['Request ID', run.provider_request_id || '—'],
                            ].map(([lbl, val]) => (
                              <div key={lbl} style={{ fontSize: '0.72rem', color: theme.textSub }}>
                                <strong style={{ color: theme.text }}>{lbl}:</strong> {val}
                              </div>
                            ))}
                          </div>

                          {/* Prompt — truncated by default with expand */}
                          {promptText && (
                            <div style={{ padding: '10px 12px', borderRadius: '10px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
                              <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', color: theme.textSub, marginBottom: '4px' }}>PROMPT</div>
                              <div style={{
                                fontSize: '0.72rem', color: theme.textSub, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                                maxHeight: aiExpandedRuns[`${run.id}-prompt`] ? 'none' : '120px', overflow: 'hidden',
                              }}>
                                {aiExpandedRuns[`${run.id}-prompt`] ? promptText : truncatedPrompt}
                              </div>
                              {promptText.length > 200 && (
                                <button onClick={() => setAiExpandedRuns(prev => ({ ...prev, [`${run.id}-prompt`]: !prev[`${run.id}-prompt`] }))}
                                  style={{ fontSize: '0.62rem', color: theme.textSub, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', opacity: 0.7 }}>
                                  {aiExpandedRuns[`${run.id}-prompt`] ? 'Collapse prompt' : 'Show full prompt'}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {filteredAiRuns.length === 0 && !aiLoading && (
                  <div style={{ color: theme.textSub, fontSize: '0.84rem' }}>
                    {aiRecentRuns.length === 0 ? 'No AI runs have been captured yet.' : 'No runs match this filter.'}
                  </div>
                )}
              </div>
            </AdminCard>
          </>
        )}

        {activeMenu === 'users' && (
          <>
            <AdminCard
              theme={theme}
              title="Access Requests"
              icon={Mail}
              accent={accessRequests.length > 0 ? '#dc2626' : '#64748b'}
              isOpen={cardOpen.access}
              onToggle={() => toggleCard('access')}
              extra={accessRequests.length > 0 ? (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '999px',
                  background: 'rgba(239,68,68,0.10)',
                  border: '1px solid rgba(239,68,68,0.26)',
                  color: '#dc2626',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                }}>
                  {accessRequests.length} pending
                </span>
              ) : null}
            >
              {accessRequests.length === 0 ? (
                <div style={{ padding: '6px 0', color: theme.textSub, fontSize: '0.86rem' }}>
                  No pending access requests.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {accessRequests.slice(0, 4).map(req => (
                    <div key={req.id} style={{ padding: '14px 16px', borderRadius: '14px', background: theme.card, border: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'center' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: theme.text, marginBottom: '3px' }}>{req.name || '(no name given)'}</div>
                        <div style={{ fontSize: '0.8rem', color: theme.textSub, marginBottom: '3px' }}>{req.email}</div>
                        {req.message && (
                          <div style={{ fontSize: '0.76rem', color: theme.textSub, lineHeight: 1.55, fontStyle: 'italic' }}>
                            "{req.message}"
                          </div>
                        )}
                      </div>
                      <Button
                        theme={theme}
                        variant="secondary"
                        iconRight={ChevronRight}
                        onClick={onGoToAccessRequests}
                        style={{ flexShrink: 0 }}
                      >
                        Open Queue
                      </Button>
                    </div>
                  ))}
                  {accessRequests.length > 4 && (
                    <Button
                      theme={theme}
                      variant="outline"
                      onClick={onGoToAccessRequests}
                    >
                      +{accessRequests.length - 4} more — open the full queue
                    </Button>
                  )}
                </div>
              )}
            </AdminCard>

            <AdminCard
              theme={theme}
              title="User Management"
              icon={UserCog}
              accent="#2563eb"
              isOpen={cardOpen.users}
              onToggle={() => toggleCard('users')}
            >
              <UsersPanel
                axiosAuth={axiosAuth}
                currentUser={currentUser}
                theme={theme}
                BACKEND_URL={BACKEND_URL}
                videoList={videoList}
              />
            </AdminCard>
            <AdminCard
              theme={theme}
              title="Access Control Board"
              icon={Shield}
              accent="#f59e0b"
              isOpen={cardOpen.acl}
              onToggle={() => toggleCard('acl')}
            >
              <div style={{ padding: '12px 16px' }}>
                <AccessControlBoard axiosAuth={axiosAuth} theme={theme} />
              </div>
            </AdminCard>
          </>
        )}

        {activeMenu === 'workspace' && (
          <AdminCard
            theme={theme}
            title="Workspace and Imports"
            icon={HardDrive}
            accent="#2563eb"
            isOpen={cardOpen.workspace}
            onToggle={() => toggleCard('workspace')}
            extra={workingDirLoading || browseLoading || configSaving ? <Icon icon={Loader} size={15} color={theme.textSub} style={{ animation: 'spin 1s linear infinite' }} /> : null}
          >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
            <Button
              theme={theme}
              variant="secondary"
              onClick={() => applyWorkingDirectory(browseData?.current_subdir || '')}
              disabled={configSaving}
            >
              Set Active Directory
            </Button>
            <Button
              theme={theme}
              variant="secondary"
              onClick={() => applyWorkingDirectory('')}
              disabled={configSaving}
            >
              Reset to Root
            </Button>
            <button
              onClick={importAll}
              disabled={importingAll || pendingImports === 0}
              style={{
                padding: '9px 13px',
                borderRadius: '12px',
                border: `1px solid ${pendingImports > 0 ? '#2563eb' : theme.border}`,
                background: pendingImports > 0 ? '#2563eb' : theme.card,
                color: pendingImports > 0 ? '#fff' : theme.textSub,
                cursor: pendingImports > 0 ? 'pointer' : 'default',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
              }}
            >
              <Icon icon={Upload} size={14} />
              {importingAll ? 'Importing…' : 'Import All Pending'}
            </button>
            <Button
              theme={theme}
              variant="secondary"
              icon={RefreshCw}
              onClick={() => loadWorkingDir().then(info => browseTo(info?.working_subdir || ''))}
            >
              Refresh Workspace
            </Button>
          </div>

          {configMessage && (
            <div style={{
              padding: '10px 12px',
              borderRadius: '12px',
              marginBottom: '14px',
              background: configMessage.type === 'ok' ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
              border: `1px solid ${configMessage.type === 'ok' ? 'rgba(34,197,94,0.26)' : 'rgba(239,68,68,0.26)'}`,
              color: configMessage.type === 'ok' ? '#166534' : '#b91c1c',
              fontSize: '0.82rem',
              fontWeight: 700,
            }}>
              {configMessage.text}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Icon icon={FolderOpen} size={15} color="#2563eb" />
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub }}>
                  Browser
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {(browseData?.breadcrumbs || []).map((crumb, idx) => (
                  <button
                    key={`${crumb.rel_path}_${idx}`}
                    onClick={() => browseTo(crumb.rel_path || '')}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '999px',
                      border: `1px solid ${theme.border}`,
                      background: idx === (browseData?.breadcrumbs || []).length - 1 ? theme.sidebar : theme.bg,
                      color: theme.text,
                      cursor: 'pointer',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                    }}
                  >
                    {crumb.name}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {(browseData?.entries || []).map(entry => (
                  <div key={entry.rel_path} style={{
                    padding: '10px 12px',
                    borderRadius: '12px',
                    background: theme.sidebar,
                    border: `1px solid ${theme.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    alignItems: 'center',
                  }}>
                    <div
                      onClick={() => entry.is_dir && browseTo(entry.rel_path)}
                      role={entry.is_dir ? 'button' : undefined}
                      tabIndex={entry.is_dir ? 0 : undefined}
                      onKeyDown={entry.is_dir ? (e => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); browseTo(entry.rel_path); }
                      }) : undefined}
                      style={{ minWidth: 0, cursor: entry.is_dir ? 'pointer' : 'default' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: theme.text }}>
                        <Icon icon={entry.is_dir ? Folder : (entry.file_type === 'mp4' ? Film : Database)} size={14} color={entry.is_dir ? '#2563eb' : theme.textSub} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: theme.textSub, marginTop: '4px' }}>
                        {entry.is_dir
                          ? `${entry.vtt_count || 0} VTT • ${entry.mp4_count || 0} MP4 • ${entry.mkv_count || 0} MKV`
                          : `${entry.file_type || 'file'}${entry.size_mb != null ? ` • ${entry.size_mb} MB` : ''}`}
                      </div>
                    </div>
                    {entry.is_dir && (
                      <Button
                        theme={theme}
                        variant="secondary"
                        size="sm"
                        onClick={() => applyWorkingDirectory(entry.rel_path)}
                        style={{ flexShrink: 0 }}
                      >
                        Set
                      </Button>
                    )}
                  </div>
                ))}
                {browseData && (browseData.entries || []).length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>This directory is empty.</div>
                )}
              </div>
            </div>

            <div style={{ padding: '16px', borderRadius: '16px', background: theme.card, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '5px' }}>
                    Import Queue
                  </div>
                  <div style={{ fontSize: '0.82rem', color: theme.textSub }}>
                    Active path: <strong style={{ color: theme.text }}>{(workingDirInfo?.display_path || '').replace(/^\/+/, '') || 'Root'}</strong>
                  </div>
                </div>
                <div style={{ fontSize: '0.78rem', color: theme.textSub }}>
                  {(workingDirInfo?.pairs || []).length} file pair{(workingDirInfo?.pairs || []).length === 1 ? '' : 's'}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {(workingDirInfo?.pairs || []).map((pair, index) => {
                  const key = pair.subtitle_file || `${pair.video_id}_${pair.part}_${index}`;
                  const busyKey = pair.subtitle_file || `${pair.video_id}_${pair.part}`;
                  const isBusy = !!importingKeys[busyKey];
                  return (
                    <div key={key} style={{
                      padding: '12px 13px',
                      borderRadius: '12px',
                      background: theme.sidebar,
                      border: `1px solid ${theme.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: theme.text, marginBottom: '4px' }}>{pair.video_id}</div>
                        <div style={{ fontSize: '0.75rem', color: theme.textSub, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Icon icon={Film} size={10} color={theme.textSub} /> Tape {pair.part} • {pair.subtitle_file}{pair.mp4_file ? ` • ${pair.mp4_file}` : ''}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: theme.textSub, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{pair.already_imported ? `${pair.line_count} lines imported` : 'Not imported yet'}</span>
                          {pair.modified && (
                            <span style={{ fontSize: '0.65rem', color: theme.textSub, opacity: 0.7 }} title={new Date(pair.modified).toLocaleString()}>
                              Modified: {new Date(pair.modified).toLocaleDateString()} {new Date(pair.modified).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                      {pair.already_imported ? (
                        <Badge
                          theme={theme}
                          status="success"
                          icon={Check}
                          uppercase={false}
                          style={{ flexShrink: 0 }}
                        >
                          Imported
                        </Badge>
                      ) : (
                        <button
                          onClick={() => importPair(pair)}
                          disabled={isBusy}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '10px',
                            border: '1px solid #2563eb',
                            background: '#2563eb',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {isBusy ? 'Importing…' : 'Import'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {workingDirInfo && (workingDirInfo.pairs || []).length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.82rem' }}>No importable subtitle files found in the active directory.</div>
                )}
              </div>
            </div>
          </div>
          </AdminCard>
        )}

        {activeMenu === 'api-keys' && (() => {
          if (!apiKeys.length && !apiKeysLoading) loadApiKeys();
          const statusColors = { database: '#34c759', environment: '#f59e0b', 'not set': '#ef4444' };
          return (<>
          <AdminCard
            theme={theme}
            title="API Keys"
            icon={Key}
            accent="#2563eb"
            isOpen={cardOpen.apiKeys}
            onToggle={() => toggleCard('apiKeys')}
          >
            <div style={{ fontSize: '0.72rem', color: theme.textSub, marginBottom: '16px', lineHeight: 1.5 }}>
              API keys can be stored in the database (preferred) or set as Docker environment variables (fallback).
              Database keys take priority over environment variables.
            </div>

            {apiKeysLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: theme.textSub }}>
                <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {apiKeys.map(k => {
                  const isEditing = apiKeyEditing === k.name;
                  const msg = apiKeyMsg[k.name];
                  return (
                    <div key={k.name} style={{
                      padding: '14px 16px', borderRadius: '12px',
                      border: `1px solid ${k.is_set ? '#34c75940' : '#ef444440'}`,
                      background: theme.card,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{k.label}</div>
                          <div style={{ fontSize: '0.68rem', color: theme.textSub }}>{k.description}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          <span style={{
                            fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                            background: `${statusColors[k.source] || '#888'}22`,
                            color: statusColors[k.source] || '#888',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>
                            {k.source}
                          </span>
                        </div>
                      </div>

                      {/* Current value (masked) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <code style={{
                          flex: 1, padding: '6px 10px', borderRadius: '6px',
                          background: theme.inputBg, border: `1px solid ${theme.border}`,
                          fontSize: '0.78rem', fontFamily: 'monospace', color: k.is_set ? theme.text : theme.textSub,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {k.is_set ? k.masked_value : 'Not configured'}
                        </code>
                        {k.is_set && (
                          <button onClick={() => testApiKey(k.name)}
                            style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                                     background: theme.card, color: theme.text, fontSize: '0.68rem',
                                     fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            <Icon icon={Zap} size={11} />Test
                          </button>
                        )}
                        <button onClick={() => {
                          if (isEditing) { setApiKeyEditing(null); setApiKeyDraft(''); }
                          else { setApiKeyEditing(k.name); setApiKeyDraft(''); }
                        }}
                          style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${isEditing ? '#2563eb' : theme.border}`,
                                   background: isEditing ? '#2563eb18' : theme.card, color: isEditing ? '#2563eb' : theme.text,
                                   fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                          {isEditing ? 'Cancel' : 'Change'}
                        </button>
                      </div>

                      {/* Edit form */}
                      {isEditing && (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <input
                              type={apiKeyVisible[k.name] ? 'text' : 'password'}
                              value={apiKeyDraft}
                              onChange={e => setApiKeyDraft(e.target.value)}
                              placeholder="Paste new key (leave empty to remove)"
                              style={{
                                width: '100%', padding: '7px 34px 7px 10px', borderRadius: '6px',
                                background: theme.inputBg, color: theme.text, border: `1px solid #2563eb`,
                                fontSize: '0.78rem', fontFamily: 'monospace', boxSizing: 'border-box',
                              }}
                            />
                            <button onClick={() => setApiKeyVisible(prev => ({ ...prev, [k.name]: !prev[k.name] }))}
                              style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                                       background: 'none', border: 'none', color: theme.textSub, cursor: 'pointer', padding: '2px' }}>
                              <Icon icon={apiKeyVisible[k.name] ? EyeOff : Eye} size={13} />
                            </button>
                          </div>
                          <button onClick={() => saveApiKey(k.name)} disabled={apiKeySaving}
                            style={{ padding: '7px 14px', borderRadius: '6px', background: '#2563eb', color: '#fff',
                                     border: 'none', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
                                     opacity: apiKeySaving ? 0.6 : 1, flexShrink: 0 }}>
                            {apiKeySaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      )}

                      {/* Status message */}
                      {msg && (
                        <div style={{
                          marginTop: '6px', fontSize: '0.72rem', fontWeight: 600,
                          color: msg.type === 'ok' ? '#34c759' : msg.type === 'err' ? '#ef4444' : '#2563eb',
                        }}>
                          {msg.type === 'ok' && <Icon icon={Check} size={12} style={{ marginRight: '4px' }} />}
                          {msg.text}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </AdminCard>

          {/* Database Connection */}
          <AdminCard
            theme={theme}
            title="Database Connection"
            icon={Database}
            accent="#0f766e"
            isOpen={cardOpen.dbConfig || false}
            onToggle={() => { toggleCard('dbConfig'); if (!dbConfig) loadDbConfig(); }}
          >
            {dbConfig && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                  {[
                    { label: 'Host', value: dbConfig.host },
                    { label: 'Database', value: dbConfig.database },
                    { label: 'Username', value: dbConfig.username },
                    { label: 'Password', value: dbConfig.password_masked },
                  ].map(item => (
                    <div key={item.label} style={{ padding: '8px 10px', borderRadius: '8px', background: theme.card, border: `1px solid ${theme.border}` }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>{item.label}</div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.text, fontFamily: 'monospace' }}>{item.value || '—'}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                    background: `${statusColors[dbConfig.source] || '#888'}22`,
                    color: statusColors[dbConfig.source] || '#888',
                    textTransform: 'uppercase',
                  }}>{dbConfig.source}</span>
                  <button onClick={() => {
                    setDbEditing(o => !o);
                    if (!dbEditing) setDbForm({ host: dbConfig.host || '', port: '5432', database: dbConfig.database || '', username: dbConfig.username || '', password: '' });
                  }}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${dbEditing ? '#0f766e' : theme.border}`, background: dbEditing ? '#0f766e18' : theme.card, color: dbEditing ? '#0f766e' : theme.text, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}>
                    {dbEditing ? 'Cancel' : 'Change'}
                  </button>
                </div>
              </div>
            )}
            {dbEditing && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '6px' }}>
                  <Input theme={theme} size="sm" value={dbForm.host} onChange={e => setDbForm(p => ({ ...p, host: e.target.value }))} placeholder="Host (e.g. 192.168.55.252)" style={{ fontFamily: 'monospace' }} />
                  <Input theme={theme} size="sm" value={dbForm.port} onChange={e => setDbForm(p => ({ ...p, port: e.target.value }))} placeholder="Port" style={{ fontFamily: 'monospace' }} />
                </div>
                <Input theme={theme} size="sm" value={dbForm.database} onChange={e => setDbForm(p => ({ ...p, database: e.target.value }))} placeholder="Database name" style={{ fontFamily: 'monospace' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <Input theme={theme} size="sm" value={dbForm.username} onChange={e => setDbForm(p => ({ ...p, username: e.target.value }))} placeholder="Username" style={{ fontFamily: 'monospace' }} />
                  <Input theme={theme} size="sm" value={dbForm.password} onChange={e => setDbForm(p => ({ ...p, password: e.target.value }))} placeholder="Password" type="password" style={{ fontFamily: 'monospace' }} />
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                  <button onClick={testDbConfig} disabled={dbSaving}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Icon icon={Zap} size={11} />Test
                  </button>
                  <button onClick={saveDbConfig} disabled={dbSaving || !dbForm.host || !dbForm.database || !dbForm.username}
                    style={{ padding: '6px 14px', borderRadius: '6px', background: '#0f766e', color: '#fff', border: 'none', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', opacity: dbSaving ? 0.6 : 1 }}>
                    {dbSaving ? 'Saving…' : 'Save & Reconnect'}
                  </button>
                </div>
              </div>
            )}
            {dbMsg && (
              <div style={{ marginTop: '6px', fontSize: '0.72rem', fontWeight: 600, color: dbMsg.type === 'ok' ? '#34c759' : dbMsg.type === 'err' ? '#ef4444' : '#2563eb' }}>
                {dbMsg.type === 'ok' && <Icon icon={Check} size={12} style={{ marginRight: '4px' }} />}
                {dbMsg.text}
              </div>
            )}
          </AdminCard>
          </>);
        })()}

        {activeMenu === 'help-menus' && (
          <AdminCard
            theme={theme}
            title="Help Menus"
            icon={HelpCircle}
            accent="#2563eb"
            isOpen={cardOpen.helpMenus}
            onToggle={() => toggleCard('helpMenus')}
          >
            {/* Tab bar */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: `1px solid ${theme.border}`, paddingBottom: '8px' }}>
              {[
                { key: 'menus', label: 'Menus', icon: HelpCircle },
                { key: 'icons', label: 'Icon Reference', icon: Eye },
                { key: 'shorthand', label: 'Shorthand Guide', icon: BookOpen },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setHelpTab(tab.key)}
                  style={{
                    padding: '5px 12px', fontSize: '0.68rem', fontWeight: 700, borderRadius: '6px',
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                    background: helpTab === tab.key ? '#2563eb' : 'transparent',
                    color: helpTab === tab.key ? '#fff' : theme.textSub,
                  }}
                >
                  <Icon icon={tab.icon} size={12} /> {tab.label}
                </button>
              ))}
            </div>

            {/* ── Icon Reference tab ── */}
            {helpTab === 'icons' && (() => {
              const q = iconSearch.toLowerCase().trim();
              const iconResults = q
                ? ALL_ICON_NAMES.filter(n => n.toLowerCase().includes(q)).slice(0, 120)
                : ALL_ICON_NAMES.slice(0, 120);
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.68rem', color: theme.textSub, flex: 1 }}>
                      {ALL_ICON_NAMES.length} Lucide icons available. Click to copy <code style={{ background: `${theme.border}66`, padding: '1px 4px', borderRadius: '3px', fontSize: '0.64rem' }}>&lt;IconName&gt;</code> shorthand.
                    </div>
                    <Input
                      theme={theme}
                      size="sm"
                      fullWidth={false}
                      value={iconSearch}
                      onChange={e => setIconSearch(e.target.value)}
                      placeholder="Search icons..."
                      style={{ width: '180px' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '4px', maxHeight: '420px', overflowY: 'auto' }}>
                    {iconResults.map(name => {
                      const LucideIcon = allLucideIcons[name];
                      if (!LucideIcon) return null;
                      const copyIconName = () => {
                        const flash = () => { setCopiedIcon(name); setTimeout(() => setCopiedIcon(c => (c === name ? null : c)), 1200); };
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(`<${name}>`).then(flash).catch(() => pushToast('admin-icon-copy-error', 'error', 'Copy failed — clipboard unavailable'));
                        } else {
                          pushToast('admin-icon-copy-error', 'error', 'Copy failed — clipboard unavailable');
                        }
                      };
                      return (
                        <div
                          key={name}
                          onClick={copyIconName}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyIconName(); } }}
                          title={copiedIcon === name ? 'Copied!' : `Click to copy <${name}>`}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                            padding: '8px 4px', borderRadius: '6px', cursor: 'pointer',
                            border: `1px solid ${theme.border}`, background: theme.card,
                            transition: 'border-color 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#2563eb'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                        >
                          <LucideIcon size={18} color="#2563eb" />
                          <div style={{ fontSize: '0.52rem', color: copiedIcon === name ? '#22c55e' : theme.text, fontWeight: copiedIcon === name ? 700 : 400, textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.2 }}>{copiedIcon === name ? 'Copied!' : name}</div>
                        </div>
                      );
                    })}
                  </div>
                  {iconResults.length >= 120 && (
                    <div style={{ fontSize: '0.6rem', color: theme.textSub, textAlign: 'center', marginTop: '6px' }}>
                      Showing first 120 results — search to narrow down
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Shorthand Guide tab ── */}
            {helpTab === 'shorthand' && (
              <div>
                <div style={{ fontSize: '0.68rem', color: theme.textSub, marginBottom: '12px', lineHeight: 1.6 }}>
                  When writing help menu content, you can insert Lucide icons inline using angle bracket shorthand.
                  Icons render at 13px with the section's accent color.
                </div>

                <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px', background: theme.card }}>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Syntax</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#2563eb', marginBottom: '6px' }}>&lt;IconName&gt;</div>
                  <div style={{ fontSize: '0.68rem', color: theme.text, lineHeight: 1.6 }}>
                    Replace <code style={{ background: `${theme.border}66`, padding: '1px 4px', borderRadius: '3px' }}>IconName</code> with the exact Lucide icon name (PascalCase). The icon renders inline with surrounding text.
                  </div>
                </div>

                <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px', background: theme.card }}>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Examples</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { input: '<Settings> — Admin panel', desc: 'Gear icon before text' },
                      { input: '<Check> Approved', desc: 'Checkmark before status' },
                      { input: '<Trash2> Delete', desc: 'Delete action with icon' },
                      { input: '<Play> / <Pause>', desc: 'Multiple icons in one line' },
                      { input: '<AlertTriangle> Warning', desc: 'Warning indicator' },
                      { input: '<Bot> AI-powered', desc: 'AI feature indicator' },
                    ].map((ex, i) => (
                      <div key={i} style={{ padding: '6px 8px', borderRadius: '6px', border: `1px solid ${theme.border}` }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.66rem', color: '#2563eb', marginBottom: '2px' }}>{ex.input}</div>
                        <div style={{ fontSize: '0.6rem', color: theme.textSub }}>{ex.desc}</div>
                        <div style={{ fontSize: '0.72rem', color: theme.text, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {renderAdminHelpText(ex.input, theme)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px', background: theme.card }}>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Tips</div>
                  <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.68rem', color: theme.text, lineHeight: 1.8 }}>
                    <li>Icon names are <strong>case-sensitive</strong> — use exact PascalCase (e.g. <code style={{ background: `${theme.border}66`, padding: '1px 4px', borderRadius: '3px' }}>HelpCircle</code> not <code style={{ background: `${theme.border}66`, padding: '1px 4px', borderRadius: '3px' }}>helpcircle</code>)</li>
                    <li>Use <code style={{ background: `${theme.border}66`, padding: '1px 4px', borderRadius: '3px' }}>\n</code> for line breaks within a section</li>
                    <li>Click any icon in the <strong>Icon Reference</strong> tab to copy its shorthand to clipboard</li>
                    <li>Unknown icon names render as plain text (no error)</li>
                  </ul>
                </div>
              </div>
            )}

            {/* ── Menus tab ── */}
            {helpTab === 'menus' && (<>
            <div style={{ fontSize: '0.68rem', color: theme.textSub, marginBottom: '12px', lineHeight: 1.5 }}>
              Manage help content shown in floating panels throughout the app. Each menu has an ID (used by the frontend to load it) and a list of titled sections. Previous versions are saved automatically.
            </div>

            {/* Existing menus */}
            {helpMenusLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: theme.textSub }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <>
                {helpMenus.map(menu => (
                  <div key={menu.id} style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.text }}>{menu.title || menu.id}</div>
                        <div style={{ fontSize: '0.6rem', color: theme.textSub, fontFamily: 'monospace' }}>
                          ID: {menu.id} · {(menu.items || []).length} section{(menu.items || []).length === 1 ? '' : 's'}
                          {menu.updated_by && <> · by {menu.updated_by}</>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => { setEditingMenu(menu.id); setMenuDraft({ id: menu.id, title: menu.title, items: [...(menu.items || [])] }); }}
                          style={{ padding: '4px 8px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', color: '#007aff', background: '#007aff14', border: '1px solid #007aff33', display: 'flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Edit3 size={10} strokeWidth={2} /> Edit
                        </button>
                        <button
                          onClick={() => { loadMenuHistory(menu.id); }}
                          style={{ padding: '4px 8px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', color: '#7c3aed', background: '#7c3aed14', border: '1px solid #7c3aed33', display: 'flex', alignItems: 'center', gap: '3px' }}
                        >
                          <History size={10} strokeWidth={2} /> History
                        </button>
                        <button
                          onClick={async () => {
                            if (!await confirm({
                              message: `Delete help menu "${menu.title || menu.id}"?`,
                              title: 'Delete help menu?',
                              confirmLabel: 'Delete',
                              destructive: true,
                            })) return;
                            axiosAuth.delete(`/admin/help-menus/${menu.id}`).then(() => loadHelpMenus());
                          }}
                          style={{ padding: '4px 8px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433', display: 'flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Trash2 size={10} strokeWidth={2} /> Delete
                        </button>
                      </div>
                    </div>
                    {/* Preview items */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(menu.items || []).map((item, i) => (
                        <span key={i} style={{ fontSize: '0.58rem', padding: '2px 7px', borderRadius: '10px', background: `${theme.border}44`, color: theme.textSub }}>
                          {item.title}
                        </span>
                      ))}
                    </div>

                    {/* History panel (inline under the menu card) */}
                    {historyMenuId === menu.id && (
                      <div style={{ marginTop: '10px', borderTop: `1px solid ${theme.border}`, paddingTop: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Version History
                          </div>
                          <button onClick={() => { setHistoryMenuId(null); setMenuHistory([]); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, padding: '2px' }}>
                            <X size={12} />
                          </button>
                        </div>
                        {historyLoading ? (
                          <div style={{ textAlign: 'center', padding: '10px' }}><Loader size={12} style={{ animation: 'spin 1s linear infinite', color: theme.textSub }} /></div>
                        ) : menuHistoryError ? (
                          <LoadFailure theme={theme} what="the version history" compact />
                        ) : menuHistory.length === 0 ? (
                          <div style={{ fontSize: '0.66rem', color: theme.textSub, fontStyle: 'italic' }}>No previous versions saved yet.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                            {menuHistory.map(h => (
                              <div key={h.history_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: '6px', border: `1px solid ${theme.border}`, fontSize: '0.64rem' }}>
                                <div>
                                  <span style={{ fontWeight: 600, color: theme.text }}>{h.title}</span>
                                  <span style={{ color: theme.textSub, marginLeft: '8px' }}>
                                    {new Date(h.saved_at).toLocaleString()} {h.saved_by && `by ${h.saved_by}`}
                                  </span>
                                  <span style={{ color: theme.textSub, marginLeft: '6px' }}>
                                    ({(h.items || []).length} section{(h.items || []).length === 1 ? '' : 's'})
                                  </span>
                                </div>
                                <button
                                  onClick={() => revertMenu(menu.id, h.history_id)}
                                  style={{ padding: '3px 8px', fontSize: '0.58rem', fontWeight: 700, borderRadius: '5px', cursor: 'pointer', color: '#f59e0b', background: '#f59e0b14', border: '1px solid #f59e0b33', display: 'flex', alignItems: 'center', gap: '3px' }}
                                >
                                  <RotateCcw size={9} /> Revert
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new / Edit form */}
                <button
                  onClick={() => {
                    if (editingMenu) { setEditingMenu(null); setMenuDraft({ id: '', title: '', items: [] }); }
                    else { setEditingMenu('__new__'); setMenuDraft({ id: '', title: '', items: [{ title: '', text: '' }] }); }
                  }}
                  style={{ padding: '6px 12px', fontSize: '0.68rem', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', color: theme.glossaryAccent || '#2563eb', background: 'none', border: `1.5px dashed ${theme.glossaryAccent || '#2563eb'}`, display: 'inline-flex', alignItems: 'center', gap: '5px', marginBottom: editingMenu ? '12px' : 0 }}
                >
                  {editingMenu ? <><X size={11} /> Cancel</> : <><Plus size={11} /> New Help Menu</>}
                </button>

                {editingMenu && (
                  <div style={{ border: `1px solid #007aff44`, borderRadius: '12px', padding: '14px', background: `${theme.card}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                      <div>
                        <label style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Menu ID</label>
                        <input
                          value={menuDraft.id}
                          onChange={e => setMenuDraft(d => ({ ...d, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                          disabled={editingMenu !== '__new__'}
                          placeholder="e.g. genogram-help"
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: `1px solid ${theme.border}`, background: editingMenu !== '__new__' ? theme.border : (theme.inputBg || theme.card), color: theme.text, fontSize: '0.74rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Title</label>
                        <Input
                          theme={theme}
                          size="sm"
                          value={menuDraft.title}
                          onChange={e => setMenuDraft(d => ({ ...d, title: e.target.value }))}
                          placeholder="e.g. How the Genogram Works"
                        />
                      </div>
                    </div>

                    <label style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                      Sections ({menuDraft.items.length})
                    </label>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {menuDraft.items.map((item, idx) => (
                      <div key={idx} style={{ border: `1px solid ${theme.border}`, borderRadius: '8px', padding: '8px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px' }}>
                          <button disabled={idx === 0} onClick={() => setMenuDraft(d => { const items = [...d.items]; [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]]; return { ...d, items }; })}
                            style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: theme.textSub, padding: '2px', opacity: idx === 0 ? 0.25 : 1 }} title="Move up">
                            <ChevronUp size={10} />
                          </button>
                          <button disabled={idx === menuDraft.items.length - 1} onClick={() => setMenuDraft(d => { const items = [...d.items]; [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]]; return { ...d, items }; })}
                            style={{ background: 'none', border: 'none', cursor: idx === menuDraft.items.length - 1 ? 'default' : 'pointer', color: theme.textSub, padding: '2px', opacity: idx === menuDraft.items.length - 1 ? 0.25 : 1 }} title="Move down">
                            <ChevronDown size={10} />
                          </button>
                          <button onClick={() => setMenuDraft(d => ({ ...d, items: d.items.filter((_, i) => i !== idx) }))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }} title="Delete section">
                            <Trash2 size={10} />
                          </button>
                        </div>
                        <input
                          value={item.title}
                          onChange={e => setMenuDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, title: e.target.value } : it) }))}
                          placeholder="Section title"
                          style={{ width: 'calc(100% - 24px)', padding: '4px 6px', borderRadius: '5px', border: `1px solid ${theme.border}`, background: theme.inputBg || theme.card, color: theme.text, fontSize: '0.72rem', fontWeight: 700, marginBottom: '4px', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                          <IconPickerToolbar
                            theme={theme}
                            onInsert={(tag) => {
                              const el = document.getElementById(`help-textarea-${idx}`);
                              if (el) {
                                const start = el.selectionStart;
                                const end = el.selectionEnd;
                                const val = item.text;
                                const newText = val.substring(0, start) + tag + val.substring(end);
                                setMenuDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, text: newText } : it) }));
                                setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + tag.length; }, 0);
                              } else {
                                setMenuDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, text: it.text + tag } : it) }));
                              }
                            }}
                          />
                          <span style={{ fontSize: '0.5rem', color: theme.textSub }}>Insert icon at cursor</span>
                        </div>
                        <textarea
                          id={`help-textarea-${idx}`}
                          value={item.text}
                          onChange={e => setMenuDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, text: e.target.value } : it) }))}
                          placeholder="Help text content... Use <IconName> for icons"
                          rows={8}
                          style={{ width: '100%', padding: '4px 6px', borderRadius: '5px', border: `1px solid ${theme.border}`, background: theme.inputBg || theme.card, color: theme.text, fontSize: '0.68rem', resize: 'vertical', boxSizing: 'border-box', flex: 1, minHeight: '140px' }}
                        />
                      </div>
                    ))}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={() => setMenuDraft(d => ({ ...d, items: [...d.items, { title: '', text: '' }] }))}
                        style={{ padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', color: theme.textSub, background: 'none', border: `1px dashed ${theme.border}`, display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Plus size={10} /> Add Section
                      </button>
                      <button
                        onClick={() => {
                          if (!menuDraft.id) { pushToast('admin-help-menu-id-required', 'error', 'Menu ID is required'); return; }
                          axiosAuth.put(`/admin/help-menus/${menuDraft.id}`, { title: menuDraft.title, items: menuDraft.items })
                            .then(() => { loadHelpMenus(); setEditingMenu(null); setMenuDraft({ id: '', title: '', items: [] }); })
                            .catch(err => pushToast('admin-help-menu-save-error', 'error', 'Save failed: ' + (err.response?.data?.detail || err.message)));
                        }}
                        style={{ padding: '5px 14px', fontSize: '0.68rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', color: '#fff', background: '#2563eb', border: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Save size={11} /> Save Menu
                      </button>
                    </div>
                  </div>
                )}

                {/* Seed default menus button */}
                {!editingMenu && (
                  <button
                    onClick={async () => {
                      const defaults = [
                        { id: 'genogram-help', title: 'How the Genogram Works', items: [
                          { title: 'People (Left Panel)', text: 'All family members extracted from the transcript. Click to select, then click the edit icon to modify. Sorted by birth year (oldest first) within each group. Use the search box to filter by name, year, place, or fate.' },
                          { title: 'Duplicates & Merging', text: 'Red "DUPLICATE" badges flag members with matching names. Click the duplicate count button to filter and see them side-by-side. Click "Keep" on the best record — the others merge into it (combining data from all sources) and are deleted. Relationships and groups update automatically.' },
                          { title: 'Chart (Top Right)', text: 'Interactive family tree. Scroll to zoom, drag to pan. Click a card to center on that person. Only members connected via Relationships appear. Card width auto-adjusts to fit the longest name.' },
                          { title: 'Relationships', text: 'Define couples (Partner A + Partner B) and their children. "Rebuild" uses stored AI hints (partner_of, parent_of, sibling_of) — no API cost. "AI Map" sends member list + transcript to Claude for deeper analysis (uses API tokens).' },
                          { title: 'GEDCOM Import & Merge', text: 'Import from genealogy software. If members exist, a merge screen shows side-by-side matches with confidence scores. GEDCOM = genealogical source of truth; AI = testimony enrichment. Merge sessions are saved — close and resume later.' },
                          { title: 'Groups', text: 'Tag members with colored labels. Colors tint chart cards and appear as badges. Use filter pills above the chart to highlight a group. Included in report and GEDCOM exports.' },
                          { title: 'Unconnected Members', text: 'Amber badge = members not in any relationship (won\'t appear in chart). Wire them via the editor, Rebuild, or AI Map.' },
                          { title: 'Card Settings', text: 'Gear icon — customize sizes, fonts, colors, visibility. Saved to your browser.' },
                          { title: 'Data Sources', text: 'AI (transcript), GEDCOM (genealogy import), MANUAL (hand-added). Source badge on each card.' },
                        ]},
                        { id: 'ui-icons-guide', title: 'Interface Icons & Navigation', items: [
                          { title: 'Navigation Bar', text: '<LayoutDashboard> — Dashboard: home view with project overview and Kanban board.\n<PenLine> — Editor: the main transcript review workspace with waveform, video, and mark cards.\n<ListChecks> — QA: quality assurance review panel for checking transcript accuracy.\n<Scissors> — Clips: propose, review, and assemble video clips for intro videos and pronunciation.\n<Settings> — Admin: system administration (admin users only).\n<DownloadCloud> — Export: download VTTs, reports, and translated files.\n<Hammer> — Tools: NotebookLM Prompt Library, AI utilities, and maintenance tools.' },
                          { title: 'Top-Right Toolbar', text: '<HelpCircle> — This help panel.\n<Activity> — Activity Log: shows running and completed background jobs.\n<Keyboard> — View and customise keyboard shortcuts for the editor.\n<Settings> — Workspace Settings: adjust text sizes and switch colour themes.\n<Sun> / <Moon> — Toggle between light and dark mode.\n<User> / <Shield> — User badge: shows your name, role, and session timer. Click for logout and preferences.' },
                          { title: 'Editor Icons', text: '<PenLine> — Manual edit indicator on a mark card.\n<Wand2> — Smart Fix: AI-corrected text.\n<CheckSquare> — Complete or approve a mark.\n<AlertTriangle> — Flag a mark for review.\n<Scissors> — Clip boundaries: set start and end trim points.\n<Play> — Play audio or video from this timecode.\n<Headphones> — Audio-only playback mode.' },
                          { title: 'Biography Page', text: '<Shield> — Vital Statistics section header.\n<Bot> — Extract from Transcript: AI extraction of biographical data.\n<Clock> — Last extraction timestamp.\n<Star> — Subject starred or favourited on dashboard.\n<MapPin> — Places and Map tab.' },
                          { title: 'Genogram Icons', text: '<GitBranch> — Family tree or genogram tab.\n<Users> — Group management.\n<Link2> — Relationship connections.\n<Pin> — Pinned or essential items.\n<HelpCircle> — Contextual help for the current panel.' },
                          { title: 'Clips & Timeline', text: '<Scissors> — Clip review mode.\n<Film> — Tape or part number indicator.\n<Video> — Video playback pill.\n<Headphones> — Audio playback pill.\n<ThumbsUp> / <ThumbsDown> — Approve or reject a clip.\n<Flag> — Flag clip for discussion.\n<ListOrdered> — Assembly queue ordering.' },
                          { title: 'File Management', text: '<DownloadCloud> — Export menu.\n<Upload> — Import files (GEDCOM, VTT).\n<Folder> — Working directory selector.\n<Trash2> — Delete (always with confirmation).\n<RotateCcw> — Undo, refresh, or rescan.\n<Copy> — Copy to clipboard.' },
                          { title: 'Status Indicators', text: 'Green <Check> — Completed or approved.\nRed accent — Error, pending review, or needs attention.\nAmber / orange — Warning, flagged, or in progress.\nPurple — AI processing cost or admin action.\nBlue — Source language or informational.\n<Loader> spinning — Background job running.' },
                        ]},
                        { id: 'admin-icons-guide', title: 'Admin Dashboard Icons', items: [
                          { title: 'Admin Navigation', text: '<Settings> — Admin panel (visible to admin users only).\n<Users> — User management and role assignment.\n<KeyRound> — API key configuration (Claude, Gemini).\n<Globe> — Translation pipeline and language management.\n<Shield> — Role-based access control badges (Admin, Editor, Viewer).\n<Mail> — Pending access requests from new users.' },
                          { title: 'Dashboard Tiles', text: '<Users> — Total registered users.\n<Clock> — Cumulative session hours across all users.\n<Activity> — Total tracked work time.\n<Bot> — AI sessions count.\n<MapPin> — Unique login locations.\n<Star> — AI processing costs.' },
                          { title: 'Tools & Maintenance', text: '<RefreshCw> — Cleanup Stale Data: cap inflated sessions, close orphaned segments, timeout stuck AI traces.\n<HelpCircle> — Help Menus: manage contextual help content shown throughout the app.\n<Hammer> — Tools section: Glossary, QR Designer, MKV Converter.\n<Loader> — Background Jobs: monitor running translation and AI tasks.' },
                        ]},
                      ];
                      if (!await confirm({
                        message: `This will create/update ${defaults.length} help menu(s):\n\n${defaults.map(d => '• ' + d.title).join('\n')}\n\nExisting menus with the same IDs will be overwritten.`,
                        title: 'Seed default help menus?',
                        confirmLabel: 'Seed',
                        destructive: false,
                      })) return;
                      Promise.all(defaults.map(m => axiosAuth.put(`/admin/help-menus/${m.id}`, { title: m.title, items: m.items })))
                        .then(() => { loadHelpMenus(); pushToast('admin-help-menus-seeded', 'success', 'Default help menus created/updated.'); });
                    }}
                    style={{ marginTop: '10px', padding: '6px 12px', fontSize: '0.68rem', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', color: '#f59e0b', background: '#f59e0b14', border: '1px solid #f59e0b33' }}
                  >
                    Seed Default Help Menus
                  </button>
                )}
              </>
            )}
            </>)}
          </AdminCard>
        )}

        {activeMenu === 'new-project' && (
          <AdminCard
            theme={theme}
            title="Register an interview"
            icon={UserPlus}
            accent="#2563eb"
            isOpen={cardOpen.newProject !== false}
            onToggle={() => toggleCard('newProject')}
          >
            <ProjectIntakePanel
              theme={theme}
              axiosAuth={axiosAuth}
              pushToast={pushToast}
              onCountChange={setIntakeCount}
            />
          </AdminCard>
        )}

        {activeMenu === 'tools' && (<>
          <AdminCard
            theme={theme}
            title="Tools and Quick Links"
            icon={Settings2}
            accent="#0f766e"
            isOpen={cardOpen.tools}
            onToggle={() => toggleCard('tools')}
          >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {[
              { label: 'Glossary', icon: BookOpen, action: onOpenGlossary, tint: '#2563eb' },
              { label: 'QR Code Designer', icon: QrCode, action: onOpenQR, tint: '#7c3aed' },
              { label: 'MKV → MP4 Converter', icon: PackageOpen, action: onOpenConverter, tint: '#d97706' },
              { label: 'Access Requests', icon: Mail, action: onGoToAccessRequests, tint: '#dc2626' },
              { label: 'Cleanup Stale Data', icon: RefreshCw, tint: '#0f766e', action: () => {
                const tok = sessionStorage.getItem('gwv_token');
                const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
                // Dry run first
                fetch(`${axiosAuth.defaults.baseURL || ''}/admin/cleanup?dry_run=true`, { method: 'POST', headers })
                  .then(r => r.json())
                  .then(async data => {
                    const parts = [];
                    if (data.stale_sessions > 0) parts.push(`${data.stale_sessions} stale sessions (open >4h)`);
                    if (data.stale_activity_segments > 0) parts.push(`${data.stale_activity_segments} orphaned activity segments`);
                    if (data.stuck_ai_traces > 0) parts.push(`${data.stuck_ai_traces} stuck AI traces`);
                    if (data.sessions_older_than_90d > 0) parts.push(`${data.sessions_older_than_90d} sessions older than 90 days (not deleted)`);
                    if (parts.length === 0) { pushToast('admin-tools-cleanup', 'info', 'Nothing to clean up — all data looks healthy.'); return; }
                    if (!await confirm({
                      message: `Found:\n\n• ${parts.join('\n• ')}\n\nClean up now?`,
                      title: 'Clean up stale data?',
                      confirmLabel: 'Clean Up',
                      destructive: true,
                    })) return;
                    fetch(`${axiosAuth.defaults.baseURL || ''}/admin/cleanup?dry_run=false`, { method: 'POST', headers })
                      .then(r => r.json())
                      .then(result => { pushToast('admin-tools-cleanup', 'success', `Cleanup complete: ${parts.join(' • ')}`); })
                      .catch(err => pushToast('admin-tools-cleanup', 'error', `Cleanup failed: ${err.message}`));
                  })
                  .catch(err => pushToast('admin-tools-cleanup', 'error', `Dry run failed: ${err.message}`));
              }},
            ].map(item => (
              <button
                key={item.label}
                onClick={item.action}
                style={{
                  minWidth: '220px',
                  flex: '1 1 220px',
                  padding: '15px 16px',
                  borderRadius: '16px',
                  border: `1px solid ${theme.border}`,
                  background: theme.card,
                  color: theme.text,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Icon icon={item.icon} size={16} color={item.tint} />
                  <span style={{ fontWeight: 800 }}>{item.label}</span>
                </div>
                <div style={{ fontSize: '0.76rem', color: theme.textSub }}>
                  {item.label === 'Cleanup Stale Data' ? 'Close stale sessions, orphaned activity segments, and stuck AI traces.' : 'Open the existing tool without leaving the admin workspace.'}
                </div>
              </button>
            ))}
          </div>
          </AdminCard>

        </>)}
      </div>
      </div>
    </div>
  );
}


// ── Background Jobs — collapsible inline panel ──────────────────────────────
function BackgroundJobsInline({ theme, axiosAuth }) {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isOpen, setIsOpen] = React.useState(false);

  const loadJobs = React.useCallback(() => {
    axiosAuth.get('/jobs')
      .then(r => setJobs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [axiosAuth]);

  React.useEffect(() => {
    loadJobs();
    const hasRunning = jobs.some(j => j.status === 'running');
    const interval = setInterval(loadJobs, hasRunning ? 3000 : 5000);
    return () => clearInterval(interval);
  }, [loadJobs, jobs.length]);

  // Auto-open when jobs are running
  React.useEffect(() => {
    if (jobs.some(j => j.status === 'running') && !isOpen) setIsOpen(true);
  }, [jobs]);

  const statusStyle = (status) => {
    const map = {
      running: { color: '#2563eb', bg: '#2563eb14', border: '#2563eb33', label: 'RUNNING' },
      complete: { color: '#16a34a', bg: '#16a34a14', border: '#16a34a33', label: 'COMPLETE' },
      failed: { color: '#ef4444', bg: '#ef444414', border: '#ef444433', label: 'FAILED' },
    };
    return map[status] || { color: '#6b7280', bg: '#6b728014', border: '#6b728033', label: status?.toUpperCase() || '?' };
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const running = jobs.filter(j => j.status === 'running');
  const failed = jobs.filter(j => j.status === 'failed');

  return (
    <div style={{ marginBottom: '8px', background: theme.sidebar, border: `1px solid ${running.length > 0 ? '#2563eb33' : theme.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      {/* Header bar — always visible */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          width: '100%', border: 'none', background: 'transparent', cursor: 'pointer',
          padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: theme.text,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon={Zap} size={13} color={running.length > 0 ? '#2563eb' : theme.textSub} />
          <span style={{ fontSize: '0.74rem', fontWeight: 700 }}>Background Jobs</span>
          {running.length > 0 && (
            <span style={{ fontSize: '0.52rem', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', color: '#2563eb', background: '#2563eb14', border: '1px solid #2563eb33' }}>
              {running.length} running
            </span>
          )}
          {failed.length > 0 && (
            <span style={{ fontSize: '0.52rem', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433' }}>
              {failed.length} failed
            </span>
          )}
          {jobs.length > 0 && running.length === 0 && failed.length === 0 && (
            <span style={{ fontSize: '0.52rem', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', color: '#16a34a', background: '#16a34a14', border: '1px solid #16a34a33' }}>
              {jobs.length} recent
            </span>
          )}
        </span>
        <Icon icon={isOpen ? ChevronDown : ChevronRight} size={14} color={theme.textSub} />
      </button>

      {/* Collapsible body */}
      {isOpen && (
        <div style={{ padding: '0 14px 10px' }}>
          {loading && jobs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '12px', color: theme.textSub, fontSize: '0.68rem' }}>Loading...</div>
          )}
          {!loading && jobs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '12px', color: theme.textSub, fontSize: '0.68rem' }}>No recent jobs</div>
          )}
          {jobs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {jobs.map(job => {
                const s = statusStyle(job.status);
                return (
                  <div key={job.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '6px 8px', borderRadius: '7px',
                    border: `1px solid ${job.status === 'running' ? '#2563eb33' : theme.border}`,
                    background: job.status === 'running' ? '#2563eb06' : theme.card,
                  }}>
                    <span style={{ fontSize: '0.5rem', fontWeight: 700, padding: '1px 5px', borderRadius: '6px', flexShrink: 0,
                      color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
                      {s.label}
                    </span>
                    <span style={{ fontSize: '0.66rem', fontWeight: 700, color: theme.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.label || job.type}
                    </span>
                    <span style={{ fontSize: '0.52rem', color: theme.textSub, flexShrink: 0 }}>
                      {formatTime(job.created_at)}{job.duration_s != null ? ` · ${formatDuration(job.duration_s)}` : ''}
                    </span>
                    {job.error && (
                      <span style={{ fontSize: '0.5rem', color: '#ef4444', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {job.error.slice(0, 60)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
